import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { parseUTCDate } from '../lib/dateUtils';

// Admin Bookings Component
const BookingsComponent = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const { toast } = useToast();

  // Debounce timer ref for search
  const searchTimeoutRef = useRef(null);

  const fetchAllBookings = async (page = 1, searchQuery = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pagination.pageSize);
      if (dateFilter.startDate) params.append('date_from', dateFilter.startDate);
      if (dateFilter.endDate) params.append('date_to', dateFilter.endDate + 'T23:59:59');
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      
      const response = await axios.get(`/admin/all-bookings?${params.toString()}`);
      
      // Handle both old (array) and new (paginated) response formats
      if (response.data.bookings) {
        setBookings(response.data.bookings || []);
        setFilteredBookings(response.data.bookings || []);
        setPagination({
          page: response.data.pagination.page,
          pageSize: response.data.pagination.page_size,
          totalCount: response.data.pagination.total_count,
          totalPages: response.data.pagination.total_pages,
          hasNext: response.data.pagination.has_next,
          hasPrev: response.data.pagination.has_prev
        });
      } else {
        // Fallback for old response format
        setBookings(response.data || []);
        setFilteredBookings(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching all bookings:', error);
      toast({
        title: "Error",
        description: "Failed to fetch bookings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    fetchAllBookings(newPage, searchTerm);
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(prev => ({ ...prev, pageSize: parseInt(newSize), page: 1 }));
  };

  // Re-fetch when pageSize changes
  useEffect(() => {
    if (pagination.pageSize) {
      fetchAllBookings(1, searchTerm);
    }
  }, [pagination.pageSize]);

  // Debounced search - triggers API call after user stops typing
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      fetchAllBookings(1, value); // Reset to page 1 when searching
    }, 500); // 500ms debounce
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const exportBookingsCSV = () => {
    if (filteredBookings.length === 0) {
      toast({
        title: "No Data",
        description: "No bookings to export",
        variant: "destructive"
      });
      return;
    }

    const headers = ['Reference', 'User', 'Email', 'Event', 'Date', 'Tee Time', 'Status', 'Amount', 'Payment Method', 'Created At'];
    const rows = filteredBookings.map(b => [
      b.booking_reference || '',
      b.user_name || `${b.user?.first_name || ''} ${b.user?.last_name || ''}`.trim(),
      b.user_email || b.user?.email || '',
      b.event_name || b.event?.name || '',
      b.event_date ? new Date(b.event_date).toLocaleDateString() : '',
      b.tee_time || '',
      b.status || '',
      b.total_amount || 0,
      b.payment_method || '',
      b.created_at ? parseUTCDate(b.created_at).toLocaleString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `bookings_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({ title: "Export Complete", description: `Exported ${filteredBookings.length} bookings` });
  };

  useEffect(() => {
    fetchAllBookings(1, searchTerm);
  }, []);

  // Re-fetch when date filter changes
  useEffect(() => {
    fetchAllBookings(1, searchTerm); // Reset to page 1 when filter changes
  }, [dateFilter.startDate, dateFilter.endDate]);

  // Just set filtered bookings to bookings (server already filtered)
  useEffect(() => {
    setFilteredBookings(bookings);
  }, [bookings]);

  const getStatusBadgeClass = (status) => {
    const classes = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  }

  const canCancelBooking = (booking) => {
    return booking.status !== 'cancelled' && booking.status !== 'completed';
  };

  const getConfiscationWarning = (booking) => {
    const teeTime = new Date(booking.tee_time);
    const now = new Date();
    const hoursUntilTeeTime = (teeTime - now) / (1000 * 60 * 60);
    
    return hoursUntilTeeTime < 48;
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`/bookings/${bookingId}`);
      
      toast({
        title: "Booking Cancelled",
        description: "The user's booking has been cancelled successfully"
      });
      
      // Refresh bookings list
      fetchAllBookings();
      setCancellingBooking(null);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Cancellation Failed",
        description: error.response?.data?.detail || "Failed to cancel booking"
      });
    }
  };;

  if (loading) {
    return <div className="text-center py-8">Loading bookings...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">All Bookings ({bookings.length})</h3>
        <div className="flex gap-2">
          <Button onClick={exportBookingsCSV} variant="outline" size="sm" className="text-emerald-600 border-emerald-600">
            📥 Export CSV
          </Button>
          <Button onClick={fetchAllBookings} variant="outline" size="sm">
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Date Range Filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-gray-50 p-3 rounded-lg">
        <div>
          <label className="text-xs text-gray-500">From Date</label>
          <Input
            type="date"
            value={dateFilter.startDate}
            onChange={(e) => setDateFilter(prev => ({...prev, startDate: e.target.value}))}
            className="text-sm"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">To Date</label>
          <Input
            type="date"
            value={dateFilter.endDate}
            onChange={(e) => setDateFilter(prev => ({...prev, endDate: e.target.value}))}
            className="text-sm"
          />
        </div>
        <div className="col-span-2 flex items-end gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setDateFilter({ startDate: '', endDate: '' })}
            className="text-gray-500"
          >
            Clear Dates
          </Button>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-gray-200">
        <svg 
          className="w-5 h-5 text-gray-400" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
          />
        </svg>
        <Input
          type="text"
          placeholder="Search all bookings by name, email, event, reference, amount, status..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          data-testid="bookings-search-input"
        />
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearchTerm(''); fetchAllBookings(1, ''); }}
            className="text-gray-400 hover:text-gray-600"
            data-testid="bookings-search-clear"
          >
            Clear
          </Button>
        )}
        {loading && searchTerm && (
          <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
        )}
      </div>

      {/* Results Count */}
      {(searchTerm || dateFilter.startDate || dateFilter.endDate) && (
        <div className="text-sm text-gray-600">
          {searchTerm ? `Found ${pagination.totalCount} bookings matching "${searchTerm}"` : `Showing ${pagination.totalCount} bookings`}
        </div>
      )}
      
      {bookings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No bookings found
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No bookings match your search "{searchTerm}"
        </div>
      ) : (
      <>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">User</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Event</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Tee Time</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Booking Date</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Players</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Amount</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Status</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Reference</th>
                <th className="border border-gray-300 px-2 py-2 text-left text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => (
                <tr key={booking.id} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-2 py-2 text-sm">
                    <div>
                      <div className="font-medium">{booking.user_name}</div>
                      <div className="text-xs text-gray-500">{booking.user_email}</div>
                      <div className="text-xs text-gray-500">{booking.user_membership}</div>
                    </div>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm">
                    <div>
                      <div className="font-medium">{booking.event_name}</div>
                      <div className="text-xs text-gray-500">
                        {booking.tee_name || '1st'} Tee - Slot {(booking.slot_index || 0) + 1}
                      </div>
                    </div>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm">
                    {new Date(booking.tee_time).toLocaleString()}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm">
                    <div>
                      <div className="font-medium">
                        {booking.created_at ? parseUTCDate(booking.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {booking.created_at ? parseUTCDate(booking.created_at).toLocaleTimeString() : ''}
                      </div>
                    </div>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm text-center">
                    {booking.players_count}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm">
                    <div>R{booking.total_amount}</div>
                    {booking.credits_used > 0 && (
                      <div className="text-xs text-green-600">Credits: R{booking.credits_used}</div>
                    )}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm">
                    <Badge className={getStatusBadgeClass(booking.status)}>
                      {booking.status}
                    </Badge>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm font-mono text-xs">
                    {booking.booking_reference}
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-sm">
                    {canCancelBooking(booking) ? (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-600 text-red-600 hover:bg-red-50"
                          >
                            Cancel
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Cancel User Booking</DialogTitle>
                          </DialogHeader>
                          <div className="py-4">
                            <p className="text-gray-600 mb-4">
                              Are you sure you want to cancel booking #{booking.booking_reference} for <strong>{booking.user_name}</strong>?
                            </p>
                            
                            {/* 48-Hour Forfeit Warning */}
                            {getConfiscationWarning(booking) && (
                              <Alert className="mb-4 border-orange-200 bg-orange-50">
                                <AlertDescription className="text-orange-800">
                                  <strong>⚠️ Within 48 Hours Notice:</strong> User will forfeit their payment and receive no free round credit. No refunds will be processed.
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            <Alert className="mb-4">
                              <AlertDescription>
                                <strong>User:</strong> {booking.user_name} ({booking.user_email})
                              </AlertDescription>
                            </Alert>
                            
                            {/* Show refund info only if MORE than 48 hours */}
                            {!getConfiscationWarning(booking) && booking.credits_used > 0 && (
                              <Alert className="mb-4">
                                <AlertDescription>
                                  R{booking.credits_used.toFixed(2)} in credits will be refunded to the user's account.
                                </AlertDescription>
                              </Alert>
                            )}
                            {!getConfiscationWarning(booking) && booking.free_round_used && (
                              <Alert className="mb-4">
                                <AlertDescription>
                                  The user's free round will be credited back to their account.
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            <div className="flex gap-3">
                              <Button
                                variant="outline"
                                onClick={() => setCancellingBooking(null)}
                                className="flex-1"
                              >
                                Keep Booking
                              </Button>
                              <Button
                                variant="destructive"
                                onClick={() => handleCancelBooking(booking.id)}
                                className="flex-1"
                              >
                                Cancel Booking
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    ) : (
                      <span className="text-xs text-gray-500">
                        {booking.status === 'cancelled' ? 'Cancelled' : 'Completed'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              Showing {pagination.totalCount > 0 ? ((pagination.page - 1) * pagination.pageSize) + 1 : 0} - {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} bookings
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Per page:</span>
              <select
                value={pagination.pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
                data-testid="bookings-page-size-select"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPrev}
                data-testid="bookings-prev-page-btn"
              >
                Previous
              </Button>
              <span className="text-sm px-3">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNext}
                data-testid="bookings-next-page-btn"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </>
      )}
    </div>
  );
};

const AdminPanel = () => {
  const { toast } = useToast();
  const { user: currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  
  // Search and filtering states
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [transactionDateFilter, setTransactionDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [adminLogDateFilter, setAdminLogDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [filteredAdminLogs, setFilteredAdminLogs] = useState([]);
  
  // Enhanced user management states
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserModal, setEditUserModal] = useState(false);
  const [bulkOperationModal, setBulkOperationModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [passwordResetModal, setPasswordResetModal] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // Add controlled tabs state

  // User Transactions modal (per-user credit history with running balance)
  const [userTxnModal, setUserTxnModal] = useState(false);
  const [userTxnLoading, setUserTxnLoading] = useState(false);
  const [userTxnTarget, setUserTxnTarget] = useState(null);
  const [userTxnList, setUserTxnList] = useState([]);
  const [userTxnFromDate, setUserTxnFromDate] = useState('');
  const [userTxnToDate, setUserTxnToDate] = useState('');
  
  // Import/Export states
  const [importModal, setImportModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  // Free Rounds Report states
  const [freeRoundsReportModal, setFreeRoundsReportModal] = useState(false);
  const [freeRoundsReport, setFreeRoundsReport] = useState(null);
  const [freeRoundsSearch, setFreeRoundsSearch] = useState(''); // Search filter for free rounds report
  const [reportDateRange, setReportDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // First day of current month
    endDate: new Date().toISOString().split('T')[0] // Today
  });
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [allocatingRounds, setAllocatingRounds] = useState(false);
  const [loadingAllocationStatus, setLoadingAllocationStatus] = useState(false);

  // Mhala Hole-in-One Competition Stats
  const [mhalaStatsModal, setMhalaStatsModal] = useState(false);
  const [mhalaStats, setMhalaStats] = useState(null);
  // Prize Giving Dinner report
  const [dinnerReportModal, setDinnerReportModal] = useState(false);
  const [dinnerReport, setDinnerReport] = useState(null);
  const [loadingMhalaStats, setLoadingMhalaStats] = useState(false);
  const [mhalaDateRange, setMhalaDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], // Start of year
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  // Handicap Import states
  const [handicapImportModal, setHandicapImportModal] = useState(false);
  const [handicapImportFile, setHandicapImportFile] = useState(null);
  const [handicapImportProgress, setHandicapImportProgress] = useState(null);
  const [handicapImportResults, setHandicapImportResults] = useState(null);
  
  // Course management states
  const [courses, setCourses] = useState([]);
  const [courseModal, setCourseModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    name: '',
    location: '',
    province: '',
    tees: []
  });
  
  // Form states for enhanced features
  const [userEditForm, setUserEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    saga_number: '',
    membership_tier: '',
    role: '',
    dh_handicap: '',
    email_verified: false
  });
  
  const [bulkOperationForm, setBulkOperationForm] = useState({
    operation: 'credits_add',
    value: 0
  });
  
  const [passwordResetForm, setPasswordResetForm] = useState({
    new_password: '',
    confirm_password: ''
  });

  // Advanced booking states
  const [adminBookingModal, setAdminBookingModal] = useState(false);
  const [waitlistModal, setWaitlistModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [waitlistData, setWaitlistData] = useState([]);
  const [paymentData, setPaymentData] = useState([]);
  
  // Guest Booking and Reserve Slot states
  const [guestBookingModal, setGuestBookingModal] = useState(false);
  const [reserveSlotModal, setReserveSlotModal] = useState(false);
  const [guestBookingForm, setGuestBookingForm] = useState({
    event_id: '',
    tee_time: '',
    tee_name: '1st',
    slot_index: 0,
    guest_name: '',
    guest_phone: '',
    cart_required: false,
    payment_status: 'pending' // pending, paid_cash, complimentary
  });
  const [reserveSlotForm, setReserveSlotForm] = useState({
    event_id: '',
    tee_time: '',
    tee_name: '1st',
    slot_index: 0,
    reserve_note: ''
  });
  
  const [adminBookingForm, setAdminBookingForm] = useState({
    user_id: '',
    event_id: '',
    tee_time: '',
    tee_name: '1st',  // Which tee (1st, 10th, etc.)
    slot_index: 0,    // Which slot (0-3) in the tee time
    players_count: 1,
    cart_required: false,
    payment_method: 'admin_booking',
    booking_notes: '',
    notify_user: true
  });

  const [partialPaymentForm, setPartialPaymentForm] = useState({
    payment_method: 'cash',
    amount: 0,
    payment_reference: '',
    notes: ''
  });
  const [loading, setLoading] = useState({
    users: true,
    events: true,
    transactions: true,
    logs: true,
    updateUser: false,
    freeRoundsReport: false,
    handicapImport: false
  });

  // Event management states
  const [editEventModal, setEditEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Form states - declare all state first
  const [newEvent, setNewEvent] = useState({
    name: '',
    description: '',
    date: '',
    start_time: '06:00',
    end_time: '18:00',
    member_fee: 0,
    non_member_fee: 0,
    cart_fee: 0,
    cart_required: false,
    max_players: 4,
    tee_time_interval: 10,
    starting_tees: '1st', // Changed to string
    free_rounds_allowed: true,
    bookings_open: true, // New field for booking control
    number_of_players: 120, // New field for auto-calculating end time
    image_url: '', // New field for event images
    course_id: '', // Link to course for playing handicap calculations
    mhala_hole_in_one_enabled: false, // Mhala Hole-in-One Competition
    mhala_hole_in_one_fee: 50, // Default R50
    // Prize Giving Dinner
    dinner_enabled: false,
    dinner_player_fee: 0,
    dinner_partner_fee: 0,
    dinner_cutoff: '',
    // Shotgun Start
    shotgun_start: false,
    shotgun_starting_hole: 1,
    shotgun_num_holes: 18
  });

  // Helper functions - declare after state
  const calculateEndTime = (startTime, numberOfPlayers, interval, teesString, maxPlayersPerSlot = 4) => {
    if (!startTime || !numberOfPlayers || !interval || !teesString) return startTime;
    
    // Parse tees to get the count
    const teesArray = teesString.split(',').map(tee => tee.trim()).filter(tee => tee);
    const numberOfTees = teesArray.length;
    
    if (numberOfTees === 0) return startTime;
    
    // Calculate players per tee, then slots per tee
    const playersPerTee = numberOfPlayers / numberOfTees;
    const slotsPerTee = Math.ceil(playersPerTee / maxPlayersPerSlot);
    const totalMinutes = slotsPerTee * interval;
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate.getTime() + totalMinutes * 60000);
    
    return `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`;
  };

  // useEffect hooks - declare after state and helper functions
  useEffect(() => {
    fetchUsers();
    fetchEvents();
    fetchTransactions();
    fetchAdminLogs();
    fetchCourses();
  }, []);

  // Update end time when relevant fields change
  useEffect(() => {
    if (newEvent.start_time && newEvent.number_of_players && newEvent.tee_time_interval && newEvent.starting_tees) {
      const calculatedEndTime = calculateEndTime(
        newEvent.start_time, 
        newEvent.number_of_players, 
        newEvent.tee_time_interval,
        newEvent.starting_tees
      );
      setNewEvent(prev => ({...prev, end_time: calculatedEndTime}));
    }
  }, [newEvent.start_time, newEvent.number_of_players, newEvent.tee_time_interval, newEvent.starting_tees]);

  // Search functionality
  useEffect(() => {
    if (!searchTerm) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user => 
        user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone.includes(searchTerm) ||
        user.saga_number.includes(searchTerm) ||
        (user.dh_number && user.dh_number.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load users"
      });
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(prev => ({ ...prev, events: false }));
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get('/courses');
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCourses([]);
    }
  };

  const handleSaveCourse = async () => {
    try {
      // Validate form
      if (!courseForm.name.trim()) {
        toast({ title: "Error", description: "Course name is required", variant: "destructive" });
        return;
      }
      if (courseForm.tees.length === 0) {
        toast({ title: "Error", description: "At least one tee configuration is required", variant: "destructive" });
        return;
      }

      const courseData = {
        name: courseForm.name,
        location: courseForm.location || null,
        province: courseForm.province || null,
        tees: courseForm.tees
      };

      if (selectedCourse) {
        // Update existing course
        await axios.put(`/admin/courses/${selectedCourse.id}`, courseData);
        toast({ title: "Success", description: "Course updated successfully" });
      } else {
        // Create new course
        await axios.post('/admin/courses', courseData);
        toast({ title: "Success", description: "Course created successfully" });
      }

      setCourseModal(false);
      setSelectedCourse(null);
      setCourseForm({ name: '', location: '', province: '', tees: [] });
      fetchCourses();
    } catch (error) {
      console.error('Error saving course:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to save course", 
        variant: "destructive" 
      });
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await axios.delete(`/admin/courses/${courseId}`);
      toast({ title: "Success", description: "Course deleted successfully" });
      fetchCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "Failed to delete course", 
        variant: "destructive" 
      });
    }
  };

  const handleEditCourse = (course) => {
    setSelectedCourse(course);
    setCourseForm({
      name: course.name || '',
      location: course.location || '',
      province: course.province || '',
      tees: course.tees || []
    });
    setCourseModal(true);
  };

  const handleAddTee = () => {
    setCourseForm(prev => ({
      ...prev,
      tees: [...prev.tees, { tee_name: '', course_rating: 72.0, slope_rating: 113, par: 72 }]
    }));
  };

  const handleRemoveTee = (index) => {
    setCourseForm(prev => ({
      ...prev,
      tees: prev.tees.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateTee = (index, field, value) => {
    setCourseForm(prev => ({
      ...prev,
      tees: prev.tees.map((tee, i) => 
        i === index ? { ...tee, [field]: value } : tee
      )
    }));
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get('/admin/transactions');
      setTransactions(response.data.transactions || response.data);
      setFilteredTransactions(response.data.transactions || response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setTransactions([]);
      setFilteredTransactions([]);
    } finally {
      setLoading(prev => ({ ...prev, transactions: false }));
    }
  };

  // Export Transactions to CSV
  const exportTransactionsCSV = () => {
    if (filteredTransactions.length === 0) {
      toast({
        title: "No Data",
        description: "No transactions to export",
        variant: "destructive"
      });
      return;
    }

    const headers = ['Date', 'User', 'Email', 'Event', 'Type', 'Amount (R)', 'Reference', 'Status'];
    const rows = filteredTransactions.map(t => [
      t.created_at ? parseUTCDate(t.created_at).toLocaleString() : '',
      t.user_name || '',
      t.user_email || '',
      t.event_name || '',
      t.transaction_type || '',
      t.amount || 0,
      t.booking_reference || '',
      t.status || 'completed'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `transactions_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({ title: "Export Complete", description: `Exported ${filteredTransactions.length} transactions` });
  };

  // Export Admin Logs to CSV
  const exportAdminLogsCSV = () => {
    const logsToExport = filteredAdminLogs.length > 0 ? filteredAdminLogs : adminLogs;
    if (logsToExport.length === 0) {
      toast({
        title: "No Data",
        description: "No admin logs to export",
        variant: "destructive"
      });
      return;
    }

    const headers = ['Date', 'Action', 'Description', 'Admin'];
    const rows = logsToExport.map(log => [
      log.created_at ? parseUTCDate(log.created_at).toLocaleString() : '',
      log.action || '',
      log.description || '',
      log.admin_name || log.admin_email || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `admin_logs_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({ title: "Export Complete", description: `Exported ${logsToExport.length} admin logs` });
  };

  // Filter transactions based on search term and date range
  useEffect(() => {
    let filtered = [...transactions];
    
    // Apply date range filter
    if (transactionDateFilter.startDate) {
      const start = new Date(transactionDateFilter.startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(t => {
        const transDate = new Date(t.created_at);
        return transDate >= start;
      });
    }
    if (transactionDateFilter.endDate) {
      const end = new Date(transactionDateFilter.endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(t => {
        const transDate = new Date(t.created_at);
        return transDate <= end;
      });
    }
    
    // Apply search filter
    if (transactionSearchTerm.trim()) {
      const term = transactionSearchTerm.toLowerCase();
      filtered = filtered.filter(transaction => {
        const userName = (transaction.user_name || '').toLowerCase();
        const userEmail = (transaction.user_email || '').toLowerCase();
        const eventName = (transaction.event_name || '').toLowerCase();
        const reference = (transaction.booking_reference || '').toLowerCase();
        const transactionType = (transaction.transaction_type || '').toLowerCase();
        const amount = (transaction.amount || 0).toString();

        return userName.includes(term) || 
               userEmail.includes(term) || 
               eventName.includes(term) || 
               reference.includes(term) ||
               transactionType.includes(term) ||
               amount.includes(term);
      });
    }

    setFilteredTransactions(filtered);
  }, [transactionSearchTerm, transactions, transactionDateFilter]);

  // Filter admin logs based on date range
  useEffect(() => {
    let filtered = [...adminLogs];
    
    if (adminLogDateFilter.startDate) {
      const start = new Date(adminLogDateFilter.startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.created_at);
        return logDate >= start;
      });
    }
    if (adminLogDateFilter.endDate) {
      const end = new Date(adminLogDateFilter.endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(log => {
        const logDate = new Date(log.created_at);
        return logDate <= end;
      });
    }
    
    setFilteredAdminLogs(filtered);
  }, [adminLogs, adminLogDateFilter]);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    try {
      // Process starting tees - split by comma and trim
      const teesArray = newEvent.starting_tees.split(',').map(tee => tee.trim()).filter(tee => tee);
      
      // Use noon (12:00) to avoid timezone shifting the date to previous day
      // When converting to UTC, noon local time won't cross date boundaries
      const eventData = {
        name: newEvent.name,
        description: newEvent.description,
        date: new Date(newEvent.date + 'T12:00:00').toISOString(),
        start_time: newEvent.start_time,
        end_time: newEvent.end_time,
        member_fee: parseFloat(newEvent.member_fee),
        non_member_fee: parseFloat(newEvent.non_member_fee),
        cart_fee: parseFloat(newEvent.cart_fee),
        cart_required: newEvent.cart_required,
        max_players: parseInt(newEvent.max_players),
        tee_time_interval: parseInt(newEvent.tee_time_interval),
        starting_tees: teesArray,
        free_rounds_allowed: newEvent.free_rounds_allowed,
        bookings_open: newEvent.bookings_open,
        total_players: parseInt(newEvent.number_of_players),
        course_id: newEvent.course_id || null,  // Link to course for playing handicap
        mhala_hole_in_one_enabled: newEvent.mhala_hole_in_one_enabled,
        mhala_hole_in_one_fee: parseFloat(newEvent.mhala_hole_in_one_fee) || 50,
        dinner_enabled: newEvent.dinner_enabled,
        dinner_player_fee: parseFloat(newEvent.dinner_player_fee) || 0,
        dinner_partner_fee: parseFloat(newEvent.dinner_partner_fee) || 0,
        dinner_cutoff: newEvent.dinner_cutoff || null,
        // Shotgun Start
        shotgun_start: newEvent.shotgun_start,
        shotgun_starting_hole: parseInt(newEvent.shotgun_starting_hole) || 1,
        shotgun_num_holes: parseInt(newEvent.shotgun_num_holes) || 18
      };

      console.log('Creating event with data:', eventData); // Debug log

      const response = await axios.post('/events', eventData);
      
      console.log('Event created successfully:', response.data); // Debug log
      
      toast({
        title: "Event Created",
        description: `${response.data.name} has been created successfully with ${response.data.available_slots} available slots`
      });
      
      // Reset form and refresh events
      setNewEvent({
        name: '',
        description: '',
        date: '',
        start_time: '06:00',
        end_time: '18:00',
        member_fee: 0,
        non_member_fee: 0,
        cart_fee: 0,
        cart_required: false,
        max_players: 4,
        tee_time_interval: 10,
        starting_tees: '1st',
        free_rounds_allowed: true,
        bookings_open: true,
        number_of_players: 120,
        image_url: '',
        course_id: '',
        mhala_hole_in_one_enabled: false,
        mhala_hole_in_one_fee: 50,
        dinner_enabled: false,
        dinner_player_fee: 0,
        dinner_partner_fee: 0,
        dinner_cutoff: '',
        shotgun_start: false,
        shotgun_starting_hole: 1,
        shotgun_num_holes: 18
      });
      
      fetchEvents();
      
    } catch (error) {
      console.error('Event creation error:', error); // Debug log
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to create event"
      });
    }
  };

  const fetchDinnerReport = async (event) => {
    try {
      const response = await axios.get(`/admin/events/${event.id}/dinner-report`);
      setDinnerReport(response.data);
      setDinnerReportModal(true);
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: "Failed to load dinner report" });
    }
  };

  const exportDinnerReportCSV = () => {
    if (!dinnerReport || !dinnerReport.attendees?.length) {
      toast({ variant: "destructive", title: "No Data", description: "No dinner attendees to export" });
      return;
    }
    const headers = ['Player', 'Email', 'Phone', 'Partner', 'Dinner Fee (R)', 'Booking Ref'];
    const rows = dinnerReport.attendees.map(a => [
      a.user_name, a.user_email, a.user_phone || '', a.partner ? 'Yes' : 'No',
      (a.dinner_fee || 0).toFixed(2), a.booking_reference
    ]);
    rows.push(['TOTAL HEADCOUNT:', dinnerReport.total_headcount, '', `Partners: ${dinnerReport.total_partners}`, dinnerReport.total_revenue.toFixed(2), '']);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `dinner_report_${dinnerReport.event_name.replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    // Populate ALL fields from the existing event, using existing values as fallback
    // For total_players: use original_total_players (calculated from tee times) which represents ORIGINAL capacity
    const totalPlayers = event.original_total_players !== undefined && event.original_total_players !== null 
      ? event.original_total_players 
      : (event.total_players !== undefined && event.total_players !== null 
        ? event.total_players 
        : 120);
    
    setNewEvent({
      name: event.name || '',
      description: event.description || '',
      date: event.date ? event.date.split('T')[0] : '', // Convert to YYYY-MM-DD format
      start_time: event.start_time || '06:00',
      end_time: event.end_time || '18:00',
      member_fee: event.member_fee !== undefined ? event.member_fee : 0,
      non_member_fee: event.non_member_fee !== undefined ? event.non_member_fee : 0,
      cart_fee: event.cart_fee !== undefined ? event.cart_fee : 0,
      cart_required: event.cart_required !== undefined ? event.cart_required : false,
      max_players: event.max_players !== undefined ? event.max_players : 4,
      tee_time_interval: event.tee_time_interval !== undefined ? event.tee_time_interval : 10,
      starting_tees: Array.isArray(event.starting_tees) ? event.starting_tees.join(', ') : (event.starting_tees || '1st'),
      free_rounds_allowed: event.free_rounds_allowed !== undefined ? event.free_rounds_allowed : true,
      bookings_open: event.bookings_open !== undefined ? event.bookings_open : true,
      number_of_players: totalPlayers,
      image_url: event.image_url || '',
      course_id: event.course_id || '',
      mhala_hole_in_one_enabled: event.mhala_hole_in_one_enabled || false,
      mhala_hole_in_one_fee: event.mhala_hole_in_one_fee !== undefined ? event.mhala_hole_in_one_fee : 50,
      dinner_enabled: event.dinner_enabled || false,
      dinner_player_fee: event.dinner_player_fee || 0,
      dinner_partner_fee: event.dinner_partner_fee || 0,
      dinner_cutoff: event.dinner_cutoff ? String(event.dinner_cutoff).slice(0, 16) : '',
      // Shotgun start fields
      shotgun_start: event.shotgun_start || false,
      shotgun_starting_hole: event.shotgun_starting_hole || 1,
      shotgun_num_holes: event.shotgun_num_holes || 18
    });
    setEditEventModal(true);
  };

  const handleUpdateEvent = async () => {
    try {
      // Use noon (12:00) to avoid timezone shifting the date to previous day
      const eventData = {
        ...newEvent,
        date: new Date(newEvent.date + 'T12:00:00').toISOString(),
        member_fee: parseFloat(newEvent.member_fee),
        non_member_fee: parseFloat(newEvent.non_member_fee),
        cart_fee: parseFloat(newEvent.cart_fee),
        max_players: parseInt(newEvent.max_players),
        tee_time_interval: parseInt(newEvent.tee_time_interval),
        starting_tees: newEvent.starting_tees.split(',').map(tee => tee.trim()).filter(tee => tee),
        total_players: parseInt(newEvent.number_of_players),
        course_id: newEvent.course_id || null,
        mhala_hole_in_one_enabled: newEvent.mhala_hole_in_one_enabled,
        mhala_hole_in_one_fee: parseFloat(newEvent.mhala_hole_in_one_fee) || 50,
        dinner_enabled: newEvent.dinner_enabled,
        dinner_player_fee: parseFloat(newEvent.dinner_player_fee) || 0,
        dinner_partner_fee: parseFloat(newEvent.dinner_partner_fee) || 0,
        dinner_cutoff: newEvent.dinner_cutoff || null,
        // Shotgun start fields
        shotgun_start: newEvent.shotgun_start,
        shotgun_starting_hole: parseInt(newEvent.shotgun_starting_hole) || 1,
        shotgun_num_holes: parseInt(newEvent.shotgun_num_holes) || 18
      };

      await axios.put(`/admin/events/${selectedEvent.id}`, eventData);
      
      toast({
        title: "Event Updated",
        description: `${newEvent.name} has been updated successfully`
      });
      
      setEditEventModal(false);
      fetchEvents();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to update event"
      });
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    if (!window.confirm(`Are you sure you want to delete event "${eventName}"? This will also delete all associated bookings.`)) {
      return;
    }
    
    try {
      await axios.delete(`/admin/events/${eventId}`);
      
      toast({
        title: "Event Deleted",
        description: "Event and all associated bookings have been deleted"
      });
      
      fetchEvents();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete event"
      });
    }
  };

  // Removed unused functions: handleCreditOperation and handleFreeRoundAdjustment

  const handleHandicapImport = async () => {
    if (!handicapImportFile) {
      toast({
        variant: "destructive",
        title: "No File Selected",
        description: "Please select a CSV file to import handicaps."
      });
      return;
    }

    try {
      setLoading(prev => ({ ...prev, handicapImport: true }));
      setHandicapImportProgress({ status: 'uploading', message: 'Uploading file...' });

      const formData = new FormData();
      formData.append('file', handicapImportFile);

      const response = await axios.post('/admin/import-handicaps', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data) {
        setHandicapImportResults(response.data);
        setHandicapImportProgress({ status: 'completed', message: 'Import completed successfully!' });
        
        toast({
          title: "Import Completed",
          description: `Successfully processed ${response.data.total_processed || 0} records.`,
        });

        // Refresh users list
        fetchUsers();
      }
    } catch (error) {
      console.error('Handicap import error:', error);
      setHandicapImportProgress({ status: 'error', message: error.response?.data?.detail || 'Import failed' });
      
      toast({
        variant: "destructive",
        title: "Import Failed",
        description: error.response?.data?.detail || "Failed to import handicaps"
      });
    } finally {
      setLoading(prev => ({ ...prev, handicapImport: false }));
    }
  };

  const handleGenerateFreeRoundsReport = async () => {
    try {
      setLoading(prev => ({ ...prev, freeRoundsReport: true }));
      
      const response = await axios.post('/admin/generate-free-rounds-report', {
        start_date: reportDateRange.startDate,
        end_date: reportDateRange.endDate
      });
      
      if (response.data) {
        setFreeRoundsReport(response.data);
        toast({
          title: "Report Generated",
          description: "Free rounds report has been generated successfully.",
        });
      }
    } catch (error) {
      console.error('Free rounds report error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to generate free rounds report"
      });
    } finally {
      setLoading(prev => ({ ...prev, freeRoundsReport: false }));
    }
  };

  // Fetch allocation status when modal opens
  const fetchAllocationStatus = async () => {
    try {
      setLoadingAllocationStatus(true);
      console.log('Fetching allocation status...');
      const response = await axios.get('/admin/allocation-status');
      console.log('Allocation status response:', response.data);
      setAllocationStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch allocation status:', error);
      // Set a default status so the banner still shows with an error message
      setAllocationStatus({
        current_month: new Date().getMonth() + 1,
        current_year: new Date().getFullYear(),
        allocation_done_this_month: false,
        users_allocated_this_month: 0,
        last_allocation: null,
        scheduler_active: false,
        next_scheduled_run: 'Error fetching status',
        allocation_day: 15,
        error: true
      });
    } finally {
      setLoadingAllocationStatus(false);
    }
  };

  // Fetch Mhala Hole-in-One Statistics
  const fetchMhalaStats = async () => {
    try {
      setLoadingMhalaStats(true);
      const params = new URLSearchParams();
      if (mhalaDateRange.startDate) params.append('date_from', mhalaDateRange.startDate);
      if (mhalaDateRange.endDate) params.append('date_to', mhalaDateRange.endDate + 'T23:59:59');
      
      const response = await axios.get(`/admin/mhala-stats?${params.toString()}`);
      setMhalaStats(response.data);
    } catch (error) {
      console.error('Failed to fetch Mhala stats:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch Mhala statistics"
      });
    } finally {
      setLoadingMhalaStats(false);
    }
  };

  // Download Mhala Stats as CSV
  const downloadMhalaCSV = () => {
    if (!mhalaStats || !mhalaStats.events || mhalaStats.events.length === 0) {
      toast({
        variant: "destructive",
        title: "No Data",
        description: "No Mhala competition data available to download"
      });
      return;
    }

    const headers = ['Event Name', 'Event Date', 'Total Entries', 'Total Revenue (R)'];
    const rows = mhalaStats.events.map(event => [
      event.event_name || 'Unknown',
      event.event_date ? new Date(event.event_date).toLocaleDateString() : 'N/A',
      event.entries || 0,
      (event.revenue || 0).toFixed(2)
    ]);

    // Add summary row
    rows.push(['', '', '', '']);
    rows.push(['TOTAL', '', mhalaStats.total_entries, mhalaStats.total_revenue.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `mhala_competition_report_${mhalaDateRange.startDate}_to_${mhalaDateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Download Started",
      description: "Mhala competition report downloaded"
    });
  };

  // Download Mhala Detailed Entries as CSV
  const downloadMhalaDetailedCSV = () => {
    if (!mhalaStats || !mhalaStats.detailed_entries || mhalaStats.detailed_entries.length === 0) {
      toast({
        variant: "destructive",
        title: "No Data",
        description: "No detailed entries available to download"
      });
      return;
    }

    const headers = ['User Name', 'Email', 'Event Name', 'Entry Date', 'Payment Method', 'Mhala Fee (R)'];
    const rows = mhalaStats.detailed_entries.map(entry => [
      entry.user_name || 'Unknown',
      entry.user_email || '',
      entry.event_name || 'Unknown',
      entry.entry_date ? new Date(entry.entry_date).toLocaleDateString() : 'N/A',
      entry.payment_method || 'Unknown',
      (entry.mhala_fee || 0).toFixed(2)
    ]);

    // Add summary row
    const totalFees = mhalaStats.detailed_entries.reduce((sum, e) => sum + (e.mhala_fee || 0), 0);
    rows.push(['', '', '', '', '', '']);
    rows.push(['TOTAL ENTRIES:', mhalaStats.detailed_entries.length, '', '', 'TOTAL:', totalFees.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `mhala_detailed_entries_${mhalaDateRange.startDate}_to_${mhalaDateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Download Started",
      description: "Mhala detailed entries downloaded"
    });
  };

  // Download Free Rounds Allocations as CSV
  const downloadFreeRoundsCSV = () => {
    if (!freeRoundsReport || !freeRoundsReport.allocations || freeRoundsReport.allocations.length === 0) {
      toast({
        variant: "destructive",
        title: "No Data",
        description: "No allocation data available to download"
      });
      return;
    }

    // Build CSV content
    const headers = ['Name', 'Email', 'Membership Tier', 'Rounds Allocated', 'Allocation Date', 'Allocation Type'];
    const rows = freeRoundsReport.allocations.map(alloc => [
      alloc.user_name || 'Unknown',
      alloc.user_email || '',
      alloc.membership_tier || 'N/A',
      alloc.rounds_allocated || 0,
      alloc.allocated_at ? parseUTCDate(alloc.allocated_at).toLocaleDateString() : 'N/A',
      alloc.allocation_type || 'manual'
    ]);

    // Create CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `free_rounds_allocations_${reportDateRange.startDate}_to_${reportDateRange.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Download Started",
      description: `Downloading ${freeRoundsReport.allocations.length} allocation records`
    });
  };
  
  // UseEffect to fetch allocation status when modal opens
  useEffect(() => {
    if (freeRoundsReportModal) {
      console.log('Free Rounds Report Modal opened, fetching allocation status...');
      fetchAllocationStatus();
    }
  }, [freeRoundsReportModal]);

  // UseEffect to fetch Mhala stats when modal opens
  useEffect(() => {
    if (mhalaStatsModal) {
      fetchMhalaStats();
    }
  }, [mhalaStatsModal]);

  // Manual trigger for monthly allocation
  const handleManualAllocation = async () => {
    if (!window.confirm('Are you sure you want to trigger monthly free round allocation? This will allocate free rounds to all eligible Silver and Gold members for the current month.')) {
      return;
    }
    
    try {
      setAllocatingRounds(true);
      const response = await axios.post('/admin/allocate-monthly-rounds');
      
      toast({
        title: "Allocation Complete",
        description: response.data.message,
      });
      
      // Refresh allocation status
      await fetchAllocationStatus();
      
      // Refresh the report if it's open
      if (freeRoundsReport) {
        await handleGenerateFreeRoundsReport();
      }
    } catch (error) {
      console.error('Manual allocation error:', error);
      toast({
        variant: "destructive",
        title: "Allocation Failed",
        description: error.response?.data?.detail || "Failed to allocate monthly rounds"
      });
    } finally {
      setAllocatingRounds(false);
    }
  };

  const handleExportUsers = async (format) => {
    try {
      const response = await axios.get(`/admin/users/export?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({
        title: "Export Complete",
        description: `Users exported successfully as ${format.toUpperCase()}`
      });
      
      setExportModal(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Export Failed",
        description: error.response?.data?.detail || "Failed to export users"
      });
    }
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a file to import"
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await axios.post('/admin/users/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const data = response.data;
      
      // Build detailed success message
      let description = `Created ${data.imported_count || 0} new users, updated ${data.updated_count || 0} existing users.`;
      
      // Show errors/warnings if any
      if (data.errors && data.errors.length > 0) {
        description += ` ${data.errors.length} warnings/errors.`;
        
        // Show errors in console for debugging
        console.log('Import warnings/errors:', data.errors);
        
        // Show errors in an alert after a short delay
        setTimeout(() => {
          const errorList = data.errors.slice(0, 20).join('\n');  // Show first 20
          const remaining = data.errors.length > 20 ? `\n... and ${data.errors.length - 20} more` : '';
          alert(`Import Warnings/Errors:\n\n${errorList}${remaining}`);
        }, 500);
      }
      
      toast({
        title: data.errors && data.errors.length > 0 ? "Import Completed with Warnings" : "Import Complete",
        description: description
      });
      
      setImportModal(false);
      setImportFile(null);
      fetchUsers();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Import Failed",
        description: error.response?.data?.detail || "Failed to import users"
      });
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`/admin/users/${userId}`);
      
      toast({
        title: "User Deleted",
        description: "User and all associated data have been deleted"
      });
      
      fetchUsers();
      fetchTransactions();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete user"
      });
    }
  };

  // Enhanced Admin Functions
  const fetchUserTransactions = async (user, fromDate = '', toDate = '') => {
    setUserTxnLoading(true);
    setUserTxnList([]);
    try {
      const params = [`user_id=${user.id}`, 'include_free_rounds=true'];
      if (fromDate) params.push(`from_date=${fromDate}`);
      if (toDate) params.push(`to_date=${toDate}`);
      const response = await axios.get(`/admin/transactions?${params.join('&')}`);
      const newestFirst = response.data || [];
      // Running balance is anchored to the user's CURRENT credits balance:
      // the most recent transaction's running_balance == user.credits_balance,
      // and each older entry shows the balance immediately AFTER that
      // transaction. This way the displayed balance always matches reality
      // even when older transactions happened in another timezone or when
      // the user's balance was manually corrected outside the tx history.
      const currentBalance = Number(user.credits_balance || 0);
      let running = currentBalance;
      const withBalance = newestFirst.map(t => {
        const balanceAfter = running;
        running = running - Number(t.amount || 0);
        return { ...t, running_balance: balanceAfter };
      });
      setUserTxnList(withBalance);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to load transactions',
        description: error.response?.data?.detail || 'Could not load user transactions'
      });
    } finally {
      setUserTxnLoading(false);
    }
  };

  const openUserTransactions = async (user) => {
    setUserTxnTarget(user);
    setUserTxnModal(true);
    setUserTxnFromDate('');
    setUserTxnToDate('');
    await fetchUserTransactions(user);
  };

  const handleEditUser = (user) => {
    console.log('handleEditUser called for:', user.first_name);
    setSelectedUser(user);
    setUserEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone,
      saga_number: user.saga_number,
      dh_number: user.dh_number || '',
      membership_tier: user.membership_tier,
      role: user.role,
      dh_handicap: user.dh_handicap || '',
      hna_handicap: user.hna_handicap || '',
      gender: user.gender || '',
      default_tee: user.default_tee || '',
      is_new_player: user.is_new_player || false,
      has_hna: user.has_hna !== false,  // Default to true if not set
      email_verified: user.email_verified,
      auto_generate_dh: false,
      add_credits_amount: '',
      add_credits_reason: '',
      remove_credits_amount: '',
      remove_credits_reason: '',
      free_rounds_new: '',
      free_rounds_reason: ''
    });
    console.log('Setting editUserModal to true');
    setEditUserModal(true);
  };

  const handleUpdateUser = async () => {
    console.log('handleUpdateUser called'); // Debug log
    setLoading(prev => ({...prev, updateUser: true}));
    try {
      let hasChanges = false;
      let updateData = {}; // Initialize updateData early
      
      // Handle DH Number assignment
      if (userEditForm.auto_generate_dh) {
        console.log('Auto-generating DH number');
        try {
          const dhResponse = await axios.post(`/admin/users/${selectedUser.id}/assign-dh-number`, {
            auto_generate: true
          });
          updateData.dh_number = dhResponse.data.dh_number;
          hasChanges = true;
        } catch (error) {
          console.error('DH number auto-generation error:', error);
          // Continue with other updates even if DH generation fails
        }
      }
      
      // Handle credit operations
      const creditOperations = [];
      if (userEditForm.add_credits_amount && userEditForm.add_credits_reason) {
        console.log('Adding credits operation');
        creditOperations.push({
          type: 'add',
          amount: parseFloat(userEditForm.add_credits_amount),
          reason: userEditForm.add_credits_reason
        });
        hasChanges = true;
      }
      if (userEditForm.remove_credits_amount && userEditForm.remove_credits_reason) {
        console.log('Adding remove credits operation');
        creditOperations.push({
          type: 'remove',
          amount: -parseFloat(userEditForm.remove_credits_amount), // Negative for removal
          reason: userEditForm.remove_credits_reason
        });
        hasChanges = true;
      }
      
      // Execute credit operations
      for (const operation of creditOperations) {
        console.log('Executing credit operation:', operation);
        await axios.put(`/admin/users/${selectedUser.id}/credits`, {
          amount: operation.amount,
          description: operation.reason
        });
      }
      
      // Handle free rounds adjustment
      const freeRoundsChanged = userEditForm.free_rounds_new != selectedUser.free_rounds;
      if (freeRoundsChanged && userEditForm.free_rounds_reason) {
        console.log('Adjusting free rounds');
        await axios.put(`/admin/users/${selectedUser.id}/free-rounds`, {
          rounds: parseInt(userEditForm.free_rounds_new) || 0,
          reason: userEditForm.free_rounds_reason
        });
        hasChanges = true;
      }
      
      // Handle profile field changes (excluding credit/free round operational fields)
      const profileFields = ['first_name', 'last_name', 'phone', 'saga_number', 'dh_number', 'membership_tier', 'role', 'dh_handicap', 'hna_handicap', 'gender', 'default_tee', 'is_new_player', 'has_hna', 'email_verified'];
      profileFields.forEach(key => {
        // Only skip dh_number if we successfully auto-generated it above
        if (key === 'dh_number' && userEditForm.auto_generate_dh && updateData.dh_number) {
          return;
        }
        if (userEditForm[key] !== selectedUser[key]) {
          const value = userEditForm[key];
          
          // Special handling for fields with strict validation
          if (key === 'dh_number') {
            // Only send dh_number if it's not empty and has a valid format
            if (value && value.trim() !== '') {
              updateData[key] = value.trim();
              hasChanges = true;
            } else {
              // For empty dh_number, only send if we're clearing an existing value
              if (selectedUser[key] && selectedUser[key].trim() !== '') {
                updateData[key] = null; // Send null to clear the field
                hasChanges = true;
              }
              // Otherwise skip empty dh_number to avoid validation error
            }
          } else if (key === 'dh_handicap' || key === 'hna_handicap') {
            // Special handling for numeric fields - don't send empty strings
            if (value && value.toString().trim() !== '') {
              updateData[key] = parseFloat(value);
              // Also set handicap last calculated date when handicap is manually updated
              updateData['handicap_last_calculated'] = new Date().toISOString();
              hasChanges = true;
            } else {
              // For empty handicap, only send null if we're clearing an existing value
              if (selectedUser[key] !== null && selectedUser[key] !== undefined) {
                updateData[key] = null; // Send null to clear the field
                hasChanges = true;
              }
              // Otherwise skip empty handicap to avoid validation error
            }
          } else {
            // For all other fields, send the value as-is
            updateData[key] = value;
            hasChanges = true;
          }
        }
      });
      
      console.log('Profile changes:', updateData);
      
      if (Object.keys(updateData).length > 0) {
        console.log('Updating profile');
        await axios.put(`/admin/users/${selectedUser.id}/profile`, updateData);
      }
      
      // Create success message
      let successMessage = "User profile updated successfully";
      const operations = [];
      if (creditOperations.length > 0) {
        operations.push(`${creditOperations.length} credit operation(s)`);
      }
      if (freeRoundsChanged && userEditForm.free_rounds_reason) {
        operations.push("free rounds adjusted");
      }
      if (userEditForm.auto_generate_dh) {
        operations.push(`DH Number: ${updateData.dh_number}`);
      }
      
      if (operations.length > 0) {
        successMessage += ` - ${operations.join(', ')}`;
      }
      
      // Check if any changes were made (profile changes OR operations)
      if (!hasChanges && operations.length === 0) {
        setLoading(prev => ({...prev, updateUser: false}));
        toast({
          title: "No Changes Detected",
          description: "All fields contain the same values as before. Make changes to update the user, or close this dialog.",
          variant: "default"
        });
        return;
      }
      
      console.log('Success:', successMessage);
      console.log('Closing modal and refreshing data...');
      
      toast({
        title: "✅ User Updated Successfully",
        description: successMessage,
        variant: "default"
      });
      
      // Close modal and refresh data
      setEditUserModal(false);
      await fetchUsers(); // Wait for user list to refresh
      fetchTransactions(); // Refresh transactions if credits were modified
      
      // Refresh current user data if the updated user is the logged-in user
      if (selectedUser.id === currentUser?.id) {
        refreshUser();
      }
    } catch (error) {
      console.error('Update user error:', error);
      
      // Provide more detailed error feedback
      let errorMessage = "Failed to update user";
      if (error.response?.status === 422) {
        errorMessage = error.response?.data?.detail || "Validation error: Please check your input data";
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.detail || "Bad request: Invalid data provided";
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied: Insufficient permissions";
      } else if (error.response?.status === 404) {
        errorMessage = "User not found";
      }
      
      toast({
        variant: "destructive",
        title: "Update Failed", 
        description: errorMessage
      });
    } finally {
      setLoading(prev => ({...prev, updateUser: false}));
    }
  };

  const handleResetPassword = async () => {
    if (passwordResetForm.new_password !== passwordResetForm.confirm_password) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Passwords do not match"
      });
      return;
    }
    
    try {
      await axios.post(`/admin/users/${selectedUser.id}/reset-password`, {
        new_password: passwordResetForm.new_password
      });
      
      toast({
        title: "Password Reset",
        description: "Password has been reset successfully"
      });
      
      setPasswordResetModal(false);
      setPasswordResetForm({ new_password: '', confirm_password: '' });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error", 
        description: error.response?.data?.detail || "Failed to reset password"
      });
    }
  };

  const handleResetMFA = async (user) => {
    if (!window.confirm(`Reset MFA for ${user.first_name} ${user.last_name}?`)) return;
    
    try {
      await axios.post(`/admin/users/${user.id}/reset-mfa`);
      
      toast({
        title: "MFA Reset",
        description: "MFA settings have been reset successfully"
      });
      
      fetchUsers();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to reset MFA"
      });
    }
  };

  const handleBulkOperation = async () => {
    if (selectedUsers.length === 0) {
      toast({ title: "No Users Selected", description: "Please select users for bulk operation" });
      return;
    }
    
    // Special confirmation for delete operation
    if (bulkOperationForm.operation === 'delete_users') {
      if (!window.confirm(`⚠️ Are you absolutely sure you want to DELETE ${selectedUsers.length} user(s)? This action CANNOT be undone and will remove all user data permanently!`)) {
        return;
      }
    }
    
    try {
      const requestData = {
        operation: bulkOperationForm.operation,
        user_ids: selectedUsers
      };
      
      // Only add value for operations that need it (not delete)
      if (bulkOperationForm.operation !== 'delete_users') {
        requestData.value = parseFloat(bulkOperationForm.value);
      }
      
      await axios.post('/admin/bulk-operations', requestData);
      
      const operationText = bulkOperationForm.operation === 'delete_users' ? 'deleted' : 'applied to';
      
      toast({
        title: "Bulk Operation Complete",
        description: `Operation ${operationText} ${selectedUsers.length} users`
      });
      
      setBulkOperationModal(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to perform bulk operation"
      });
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const fetchAdminLogs = async () => {
    setLoading(prev => ({ ...prev, logs: true }));
    try {
      console.log('🔍 Fetching admin logs from /api/admin/logs...');
      const response = await axios.get('/admin/logs');
      console.log('✅ Admin logs response:', response.data);
      const logs = response.data.logs || response.data;
      console.log('📊 Setting admin logs:', logs.length, 'logs found');
      setAdminLogs(logs);
    } catch (error) {
      console.error('❌ Failed to fetch admin logs:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      setAdminLogs([]);
      toast({
        title: "Error",
        description: "Failed to load admin logs",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, logs: false }));
    }
  };

  // Advanced Booking Functions
  const handleAdminBooking = async () => {
    try {
      await axios.post('/admin/bookings', adminBookingForm);
      
      toast({
        title: "Admin Booking Created",
        description: "Booking has been created successfully"
      });
      
      setAdminBookingModal(false);
      setAdminBookingForm({
        user_id: '',
        event_id: '',
        tee_time: '',
        tee_name: '1st',  // Reset to default
        slot_index: 0,    // Reset to default
        players_count: 1,
        cart_required: false,
        payment_method: 'admin_booking',
        booking_notes: '',
        notify_user: true
      });
      
      // Refresh relevant data
      fetchUsers();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to create admin booking"
      });
    }
  };

  // Guest Booking Handler
  const handleGuestBooking = async () => {
    try {
      if (!guestBookingForm.guest_name.trim()) {
        toast({
          variant: "destructive",
          title: "Validation Error",
          description: "Guest name is required"
        });
        return;
      }
      
      await axios.post('/admin/bookings/guest', guestBookingForm);
      
      toast({
        title: "Guest Booking Created",
        description: `Booking created for ${guestBookingForm.guest_name}`
      });
      
      setGuestBookingModal(false);
      setGuestBookingForm({
        event_id: '',
        tee_time: '',
        tee_name: '1st',
        slot_index: 0,
        guest_name: '',
        guest_phone: '',
        cart_required: false,
        payment_status: 'pending'
      });
      
      fetchUsers(); // Refresh data
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to create guest booking"
      });
    }
  };

  // Reserve Slot Handler
  const handleReserveSlot = async () => {
    try {
      await axios.post('/admin/bookings/reserve', reserveSlotForm);
      
      toast({
        title: "Slot Reserved",
        description: "Slot has been reserved successfully"
      });
      
      setReserveSlotModal(false);
      setReserveSlotForm({
        event_id: '',
        tee_time: '',
        tee_name: '1st',
        slot_index: 0,
        reserve_note: ''
      });
      
      fetchUsers(); // Refresh data
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to reserve slot"
      });
    }
  };

  // Release Reservation Handler
  const handleReleaseReservation = async (bookingId) => {
    try {
      await axios.post(`/admin/bookings/release-reservation/${bookingId}`);
      
      toast({
        title: "Reservation Released",
        description: "Slot is now available for booking"
      });
      
      fetchUsers(); // Refresh data
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to release reservation"
      });
    }
  };

  const fetchWaitlist = async (eventId) => {
    try {
      const response = await axios.get(`/admin/waitlist/${eventId}`);
      setWaitlistData(response.data);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
    }
  };

  const handlePromoteFromWaitlist = async (waitlistId) => {
    try {
      await axios.post(`/admin/waitlist/${waitlistId}/promote`);
      
      toast({
        title: "Promoted from Waitlist",
        description: "User has been promoted to confirmed booking"
      });
      
      // Refresh waitlist data
      if (selectedBooking?.event_id) {
        fetchWaitlist(selectedBooking.event_id);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to promote from waitlist"
      });
    }
  };

  const handleRemoveFromWaitlist = async (waitlistId) => {
    if (!window.confirm('Remove this user from the waitlist?')) return;
    
    try {
      await axios.delete(`/admin/waitlist/${waitlistId}`);
      
      toast({
        title: "Removed from Waitlist",
        description: "User has been removed from waitlist"
      });
      
      // Refresh waitlist data
      if (selectedBooking?.event_id) {
        fetchWaitlist(selectedBooking.event_id);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to remove from waitlist"
      });
    }
  };

  const fetchBookingPayments = async (bookingId) => {
    try {
      const response = await axios.get(`/admin/bookings/${bookingId}/payments`);
      setPaymentData(response.data);
    } catch (error) {
      console.error('Failed to fetch booking payments:', error);
    }
  };

  const handleAddPayment = async () => {
    try {
      await axios.post(`/admin/bookings/${selectedBooking.id}/add-payment`, partialPaymentForm);
      
      toast({
        title: "Payment Added",
        description: "Payment has been processed successfully"
      });
      
      setPartialPaymentForm({
        payment_method: 'cash',
        amount: 0,
        payment_reference: '',
        notes: ''
      });
      
      // Refresh payment data
      fetchBookingPayments(selectedBooking.id);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.detail || "Failed to add payment"
      });
    }
  };

  const getMembershipTierBadge = (tier) => {
    const config = {
      'non-member': { className: 'bg-gray-100 text-gray-600', label: 'Non-Member' },
      green: { className: 'bg-green-100 text-green-800', label: 'Green' },
      silver: { className: 'bg-gray-100 text-gray-800', label: 'Silver' },
      gold: { className: 'bg-yellow-100 text-yellow-800', label: 'Gold' },
      special: { className: 'bg-purple-100 text-purple-800', label: '🎯 Special' }
    };
    
    const tierConfig = config[tier] || config['non-member'];
    return <Badge className={tierConfig.className}>{tierConfig.label}</Badge>;
  };

  const formatDate = (dateString) => {
    const d = parseUTCDate(dateString);
    return d ? d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) : '';
  };

  const formatDateTime = (dateString) => {
    const d = parseUTCDate(dateString);
    return d ? d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }) : '';
  };

  // Build a per-transaction payment-breakdown summary from the enriched
  // booking fields the backend returns alongside each credit transaction.
  // Returns an array of short human-readable lines (e.g. ["Credits: R150",
  // "Card: R250", "Cart fee R100 (card)"]) plus tags like "Free round".
  const buildTransactionBreakdown = (t) => {
    const lines = [];
    if (t.transaction_type === 'purchase') {
      // Credit top-up
      lines.push(`Credit top-up${t.payment_id ? ` (ref: ${t.payment_id})` : ''}`);
      return lines;
    }
    if (t.transaction_type === 'refund') {
      lines.push(`Refund to credits${t.includes_cart_refund ? ' (incl. cart fee)' : ''}`);
      return lines;
    }
    if (t.transaction_type === 'admin_adjustment') {
      // Show WHO did the adjustment and the reason.
      const sign = Number(t.amount || 0) >= 0 ? 'added' : 'removed';
      const absAmt = Math.abs(Number(t.amount || 0)).toFixed(2);
      lines.push(
        `Admin ${sign} R${absAmt} credits` +
        (t.admin_name ? ` by ${t.admin_name}` : '')
      );
      if (t.description) lines.push(`Reason: ${t.description}`);
      return lines;
    }
    if (t.transaction_type === 'admin_recovery') {
      // The description already begins with "Admin recovered booking <ref> ..."
      // so we only need to append who did it.
      const base = t.description
        ? t.description
        : 'Admin recovered booking';
      lines.push(`${base}${t.admin_name ? ` (by ${t.admin_name})` : ''}`);
      return lines;
    }
    if (t.transaction_type === 'free_round_adjustment') {
      lines.push(`Admin set free rounds${t.free_rounds_new_total != null ? ` to ${t.free_rounds_new_total}` : ''}${t.admin_name ? ` (by ${t.admin_name})` : ''}`);
      if (t.description && t.description !== `Free rounds set to ${t.free_rounds_new_total}`) {
        lines.push(t.description);
      }
      return lines;
    }
    if (t.transaction_type === 'free_round_monthly') {
      lines.push(t.description || 'Monthly free round allocation');
      return lines;
    }
    if (t.transaction_type === 'free_round_usage') {
      lines.push(t.description || 'Free round used');
      return lines;
    }
    // usage / booking payment breakdown
    const total = Number(t.booking_total_amount || 0);
    const creditsUsed = Number(t.booking_credits_used || 0);
    const freeRound = !!t.booking_free_round_used;
    const paymentMethod = (t.booking_payment_method || '').toLowerCase();
    const cartFee = Number(t.booking_cart_fee_paid || 0);
    const cartMethod = (t.booking_cart_payment_method || '').toLowerCase();
    const mhalaFee = Number(t.booking_mhala_fee || 0);
    const mhalaCredits = Number(t.booking_mhala_credits_used || 0);
    const mhalaCard = Number(t.booking_mhala_card_amount || 0);
    const mhalaMethod = (t.booking_mhala_payment_method || '').toLowerCase();

    if (freeRound) lines.push('Free round used');

    // Green fee portion
    if (creditsUsed > 0 && total > creditsUsed && paymentMethod === 'card') {
      lines.push(`Credits: R${creditsUsed.toFixed(2)} + Card: R${(total - creditsUsed).toFixed(2)}`);
    } else if (creditsUsed > 0) {
      lines.push(`Credits used: R${creditsUsed.toFixed(2)}`);
    } else if (total > 0 && paymentMethod === 'card') {
      lines.push(`Card: R${total.toFixed(2)}`);
    } else if (total > 0 && paymentMethod) {
      lines.push(`${paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1)}: R${total.toFixed(2)}`);
    }

    if (cartFee > 0) {
      const cm = cartMethod === 'credit' ? 'credits' : (cartMethod === 'card' ? 'card' : cartMethod || 'card');
      lines.push(`Cart fee R${cartFee.toFixed(2)} (${cm})`);
    }

    if (mhalaFee > 0) {
      const parts = [];
      if (mhalaCredits > 0) parts.push(`R${mhalaCredits.toFixed(2)} credits`);
      if (mhalaCard > 0) parts.push(`R${mhalaCard.toFixed(2)} card`);
      if (!parts.length && mhalaMethod) parts.push(mhalaMethod);
      lines.push(`Mhala R${mhalaFee.toFixed(2)}${parts.length ? ` (${parts.join(' + ')})` : ''}`);
    }

    if (!lines.length) {
      // No booking linkage was found (booking may have been deleted) — fall
      // back to the transaction's own description so the admin still has
      // context instead of a bare "Payment: credits".
      if (t.description) {
        lines.push(t.description);
      } else if (t.booking_id) {
        lines.push(`Booking ${t.booking_id.slice(0, 8)}… (record no longer available)`);
      } else {
        lines.push(`Payment: ${paymentMethod || 'credits'}`);
      }
    }
    return lines;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-1 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-8 overflow-x-hidden">
        <div className="mb-4 sm:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="admin-panel-title">
                Admin Panel
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Manage users, events, and system settings</p>
            </div>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/dashboard'}
              className="flex items-center gap-2"
            >
              <span>←</span>
              Back to Dashboard
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-1">
            <TabsTrigger value="users" className="text-xs sm:text-sm">Users</TabsTrigger>
            <TabsTrigger value="events" className="text-xs sm:text-sm">Events</TabsTrigger>
            <TabsTrigger value="bookings" className="text-xs sm:text-sm">Bookings</TabsTrigger>
            <TabsTrigger value="transactions" className="text-xs sm:text-sm">Transactions</TabsTrigger>
            <TabsTrigger value="courses" className="text-xs sm:text-sm">Courses</TabsTrigger>
            <TabsTrigger value="logs" className="text-xs sm:text-sm">Admin Logs</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Bulk Operations Bar */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium">
                        {selectedUsers.length} user(s) selected
                      </span>
                      {selectedUsers.length > 0 && (
                        <Button
                          size="sm"
                          onClick={() => setBulkOperationModal(true)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          Bulk Operations
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedUsers([])}
                      >
                        Clear Selection
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setFreeRoundsReportModal(true)}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        📊 Free Rounds Report
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setMhalaStatsModal(true)}
                        className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                        data-testid="mhala-stats-btn"
                      >
                        🏆 Mhala Competition
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setHandicapImportModal(true)}
                        className="text-green-600 border-green-600 hover:bg-green-50"
                      >
                        🏌️ Import Handicaps
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Management */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>User Management</CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setImportModal(true)}
                        className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      >
                        Import Users
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExportModal(true)}
                        className="text-green-600 border-green-600 hover:bg-green-50"
                      >
                        Export Users
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-4">
                    <Input
                      type="text"
                      placeholder="Search users by name, email, phone, SAGA, or DH number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="max-w-md"
                    />
                    <span className="text-sm text-gray-500">
                      Showing {filteredUsers.length} of {users.length} users
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading.users ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-gray-500">Loading users...</div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredUsers.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 overflow-hidden">
                          <div className="flex items-center gap-4">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.id)}
                              onChange={() => toggleUserSelection(user.id)}
                              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold">{user.first_name} {user.last_name}</h3>
                                {getMembershipTierBadge(user.membership_tier)}
                                <Badge className={user.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}>
                                  {user.role}
                                </Badge>
                                {user.dh_number && (
                                  <Badge className="bg-purple-100 text-purple-800">
                                    {user.dh_number}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 space-y-1 break-words">
                                <div>📧 {user.email} {user.email_verified ? '✅' : '❌'}</div>
                                <div>📱 {user.phone} • SAGA: {user.saga_number}</div>
                                <div>
                                  💰 Credits: R{user.credits_balance?.toFixed(2) || '0.00'} • 
                                  🎟️ Free: {user.free_rounds || 0} • 
                                  🔐 MFA: {user.mfa_enabled ? '✅' : '❌'}
                                </div>
                                {(user.dh_handicap || user.hna_handicap) && (
                                  <div>🏌️ DHG: {user.dh_handicap || '-'} ({user.dh_95_percent || '-'}) • HNA: {user.hna_handicap || '-'} ({user.hna_95_percent || '-'})</div>
                                )}
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {user.gender && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{user.gender === 'male' ? '♂️ Male' : '♀️ Female'}</span>
                                  )}
                                  {user.default_tee && (
                                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">⛳ {user.default_tee}</span>
                                  )}
                                  {user.is_new_player && (
                                    <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded">🆕 New</span>
                                  )}
                                  {user.has_hna === false && (
                                    <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-800 rounded">Non-HNA</span>
                                  )}
                                </div>
                                <div className="text-xs">📅 Joined: {formatDate(user.created_at)}</div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Mobile-Friendly Action Buttons - Below User Info */}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('Edit button clicked for:', user.first_name);
                                  handleEditUser(user);
                                }}
                                onPointerDown={(e) => {
                                  e.stopPropagation(); // This prevents tab navigation!
                                }}
                                className="text-emerald-600 border-emerald-600 hover:bg-emerald-50 flex-1 min-w-0"
                                type="button"
                                style={{ pointerEvents: 'auto' }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setPasswordResetModal(true);
                                }}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50 flex-1 min-w-0"
                              >
                                Reset PW
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openUserTransactions(user)}
                                data-testid={`user-transactions-${user.id}`}
                                className="text-amber-700 border-amber-500 hover:bg-amber-50 flex-1 min-w-0"
                              >
                                Transactions
                              </Button>
                              {user.mfa_enabled && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleResetMFA(user)}
                                  className="text-orange-600 border-orange-600 hover:bg-orange-50 flex-1 min-w-0"
                                >
                                  Reset MFA
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteUser(user.id, user.username)}
                                data-testid={`delete-user-${user.id}`}
                                className="flex-1 min-w-0"
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Create Event Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Create New Event</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleCreateEvent} className="space-y-4">
                    <div>
                      <Label htmlFor="event-name">Event Name</Label>
                      <Input
                        id="event-name"
                        value={newEvent.name}
                        onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                        required
                        data-testid="event-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="event-description">Description</Label>
                      <Textarea
                        id="event-description"
                        value={newEvent.description}
                        onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="event-image-url">Event Image URL (Optional)</Label>
                      <Input
                        id="event-image-url"
                        type="url"
                        value={newEvent.image_url}
                        onChange={(e) => setNewEvent({...newEvent, image_url: e.target.value})}
                        placeholder="https://example.com/event-image.jpg"
                        data-testid="event-image-url-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add a URL to display an image for this event on the events page
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="event-date">Event Date</Label>
                      <Input
                        id="event-date"
                        type="date"
                        value={newEvent.date}
                        onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                        required
                        data-testid="event-date-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="start-time">Start Time</Label>
                        <Input
                          id="start-time"
                          type="time"
                          value={newEvent.start_time}
                          onChange={(e) => setNewEvent({...newEvent, start_time: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="number-of-players">Number of Players</Label>
                        <Input
                          id="number-of-players"
                          type="number"
                          min="4"
                          step="4"
                          value={newEvent.number_of_players}
                          onChange={(e) => setNewEvent({...newEvent, number_of_players: parseInt(e.target.value)})}
                          required
                          placeholder="e.g., 120"
                          data-testid="number-of-players-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">End time will be auto-calculated</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="end-time">End Time (Auto-calculated)</Label>
                      <Input
                        id="end-time"
                        type="time"
                        value={newEvent.end_time}
                        onChange={(e) => setNewEvent({...newEvent, end_time: e.target.value})}
                        className="bg-gray-50"
                        data-testid="end-time-display"
                      />
                      <p className="text-xs text-gray-500 mt-1">Calculated based on players and interval. You can override if needed.</p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="member-fee">Member Fee (R)</Label>
                        <Input
                          id="member-fee"
                          type="number"
                          step="0.01"
                          min="0"
                          value={newEvent.member_fee}
                          onChange={(e) => setNewEvent({...newEvent, member_fee: e.target.value})}
                          required
                          data-testid="member-fee-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="non-member-fee">Non-member Fee (R)</Label>
                        <Input
                          id="non-member-fee"
                          type="number"
                          step="0.01"
                          min="0"
                          value={newEvent.non_member_fee}
                          onChange={(e) => setNewEvent({...newEvent, non_member_fee: e.target.value})}
                          required
                          data-testid="non-member-fee-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="cart-fee">Cart Fee (R)</Label>
                        <Input
                          id="cart-fee"
                          type="number"
                          step="0.01"
                          min="0"
                          value={newEvent.cart_fee}
                          onChange={(e) => setNewEvent({...newEvent, cart_fee: e.target.value})}
                          required
                        />
                      </div>
                      <div className="pt-6">
                        <Label htmlFor="cart-option">Cart Options</Label>
                        <select
                          id="cart-option"
                          value={newEvent.cart_fee > 0 ? (newEvent.cart_required ? 'required' : 'optional') : 'not_available'}
                          onChange={(e) => {
                            if (e.target.value === 'required') {
                              setNewEvent({...newEvent, cart_required: true, cart_fee: newEvent.cart_fee || 50});
                            } else if (e.target.value === 'not_available') {
                              setNewEvent({...newEvent, cart_required: false, cart_fee: 0});
                            } else {
                              setNewEvent({...newEvent, cart_required: false, cart_fee: newEvent.cart_fee || 50});
                            }
                          }}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="required">🏌️ Cart Included (Mandatory - price included in total)</option>
                          <option value="not_available">🚫 No Cart Available</option>
                        </select>
                        <p className="text-sm text-gray-500 mt-1">
                          {newEvent.cart_fee > 0 && newEvent.cart_required && "Cart fee will be automatically added to all bookings"}
                          {newEvent.cart_fee === 0 && "No cart option available for this event"}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="starting-tees">Starting Tees (comma-separated)</Label>
                        <Input
                          id="starting-tees"
                          type="text"
                          value={newEvent.starting_tees}
                          onChange={(e) => setNewEvent({...newEvent, starting_tees: e.target.value})}
                          placeholder="e.g., 1st, 10th or 1st"
                          required
                          data-testid="starting-tees-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">Enter tee names separated by commas</p>
                      </div>
                      <div className="flex items-center space-x-2 pt-6">
                        <input
                          type="checkbox"
                          id="free-rounds-allowed"
                          checked={newEvent.free_rounds_allowed}
                          onChange={(e) => setNewEvent({...newEvent, free_rounds_allowed: e.target.checked})}
                          className="rounded"
                        />
                        <Label htmlFor="free-rounds-allowed">Allow Free Rounds</Label>
                      </div>
                      <div className="flex items-center space-x-2 pt-6">
                        <input
                          type="checkbox"
                          id="bookings-open"
                          checked={newEvent.bookings_open}
                          onChange={(e) => setNewEvent({...newEvent, bookings_open: e.target.checked})}
                          className="rounded"
                        />
                        <Label htmlFor="bookings-open">Bookings Open</Label>
                      </div>
                    </div>
                    
                    {/* Course Selection for Playing Handicap */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <Label htmlFor="course-select" className="font-semibold text-blue-900">Course (for Playing Handicap)</Label>
                      <Select
                        value={newEvent.course_id || "none"}
                        onValueChange={(value) => setNewEvent({...newEvent, course_id: value === "none" ? "" : value})}
                      >
                        <SelectTrigger className="mt-2" data-testid="course-select">
                          <SelectValue placeholder="Select a course (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No course selected</SelectItem>
                          {courses.map(course => (
                            <SelectItem key={course.id} value={course.id}>
                              {course.name} {course.location && `(${course.location})`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-blue-700 mt-2">
                        Linking a course enables playing handicap calculations on the tee sheet based on course rating and slope.
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="interval">Tee Time Interval (min)</Label>
                        <Select
                          value={newEvent.tee_time_interval.toString()}
                          onValueChange={(value) => setNewEvent({...newEvent, tee_time_interval: parseInt(value)})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[6, 8, 10, 12, 15, 20, 30].map(num => (
                              <SelectItem key={num} value={num.toString()}>{num} min</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="max-players">Max Players per Slot</Label>
                        <div className="pt-2 text-sm text-gray-600">
                          Fixed at 4 players per slot
                        </div>
                      </div>
                    </div>
                    
                    {/* Mhala Hole-in-One Competition Section */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-testid="mhala-section">
                      <div className="flex items-center space-x-2 mb-3">
                        <input
                          type="checkbox"
                          id="mhala-enabled"
                          checked={newEvent.mhala_hole_in_one_enabled}
                          onChange={(e) => setNewEvent({...newEvent, mhala_hole_in_one_enabled: e.target.checked})}
                          className="rounded"
                          data-testid="mhala-enabled-checkbox"
                        />
                        <Label htmlFor="mhala-enabled" className="font-semibold text-yellow-800">
                          🏆 Mhala Hole-in-One Competition
                        </Label>
                      </div>
                      {newEvent.mhala_hole_in_one_enabled && (
                        <div className="ml-6">
                          <Label htmlFor="mhala-fee" className="text-sm text-yellow-700">Fee (R)</Label>
                          <Input
                            id="mhala-fee"
                            type="number"
                            value={newEvent.mhala_hole_in_one_fee}
                            onChange={(e) => setNewEvent({...newEvent, mhala_hole_in_one_fee: parseFloat(e.target.value) || 0})}
                            className="w-32 mt-1"
                            min="0"
                            step="0.01"
                            data-testid="mhala-fee-input"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Prize Giving Dinner Section */}
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4" data-testid="dinner-section">
                      <div className="flex items-center space-x-2 mb-3">
                        <input
                          type="checkbox"
                          id="dinner-enabled"
                          checked={newEvent.dinner_enabled}
                          onChange={(e) => setNewEvent({...newEvent, dinner_enabled: e.target.checked})}
                          className="rounded"
                          data-testid="dinner-enabled-checkbox"
                        />
                        <Label htmlFor="dinner-enabled" className="font-semibold text-purple-800">
                          🍽️ Prize Giving Dinner (compulsory for all players — partner optional)
                        </Label>
                      </div>
                      {newEvent.dinner_enabled && (
                        <div className="ml-6 grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="dinner-player-fee" className="text-sm text-purple-700">Player Fee (R)</Label>
                            <Input
                              id="dinner-player-fee"
                              type="number"
                              value={newEvent.dinner_player_fee}
                              onChange={(e) => setNewEvent({...newEvent, dinner_player_fee: parseFloat(e.target.value) || 0})}
                              className="w-32 mt-1"
                              min="0"
                              step="0.01"
                              data-testid="dinner-player-fee-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="dinner-partner-fee" className="text-sm text-purple-700">Partner Fee (R)</Label>
                            <Input
                              id="dinner-partner-fee"
                              type="number"
                              value={newEvent.dinner_partner_fee}
                              onChange={(e) => setNewEvent({...newEvent, dinner_partner_fee: parseFloat(e.target.value) || 0})}
                              className="w-32 mt-1"
                              min="0"
                              step="0.01"
                              data-testid="dinner-partner-fee-input"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label htmlFor="dinner-cutoff" className="text-sm text-purple-700">Change Cut-off (optional — players can change their dinner choice until this time; defaults to event date)</Label>
                            <Input
                              id="dinner-cutoff"
                              type="datetime-local"
                              value={newEvent.dinner_cutoff}
                              onChange={(e) => setNewEvent({...newEvent, dinner_cutoff: e.target.value})}
                              className="w-64 mt-1"
                              data-testid="dinner-cutoff-input"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Shotgun Start Section */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="shotgun-section">
                      <div className="flex items-center space-x-2 mb-3">
                        <input
                          type="checkbox"
                          id="shotgun-start"
                          checked={newEvent.shotgun_start}
                          onChange={(e) => setNewEvent({...newEvent, shotgun_start: e.target.checked})}
                          className="rounded"
                          data-testid="shotgun-start-checkbox"
                        />
                        <Label htmlFor="shotgun-start" className="font-semibold text-blue-800">
                          🎯 Shotgun Start (All groups start same time, different holes)
                        </Label>
                      </div>
                      {newEvent.shotgun_start && (
                        <div className="ml-6 grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="shotgun-starting-hole" className="text-sm text-blue-700">Starting Hole</Label>
                            <Select
                              value={newEvent.shotgun_starting_hole?.toString() || '1'}
                              onValueChange={(value) => setNewEvent({...newEvent, shotgun_starting_hole: parseInt(value)})}
                            >
                              <SelectTrigger className="mt-1" data-testid="shotgun-starting-hole">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1">Hole 1</SelectItem>
                                <SelectItem value="10">Hole 10</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="shotgun-num-holes" className="text-sm text-blue-700">Number of Holes</Label>
                            <Select
                              value={newEvent.shotgun_num_holes?.toString() || '18'}
                              onValueChange={(value) => setNewEvent({...newEvent, shotgun_num_holes: parseInt(value)})}
                            >
                              <SelectTrigger className="mt-1" data-testid="shotgun-num-holes">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="9">9 Holes</SelectItem>
                                <SelectItem value="18">18 Holes</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <p className="col-span-2 text-xs text-blue-600 mt-1">
                            Shotgun creates {newEvent.shotgun_num_holes || 18} tee slots (one per hole), all starting at {newEvent.start_time}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="create-event-btn"
                    >
                      Create Event
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Events List */}
              <Card>
                <CardHeader>
                  <CardTitle>All Events ({events.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading.events ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                      {events.map((event) => (
                        <div key={event.id} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold">{event.name}</h4>
                              <p className="text-sm text-gray-600">{formatDate(event.date)} • {event.start_time} - {event.end_time}</p>
                              <p className="text-sm text-gray-500">
                                Member: R{event.member_fee} | Non-member: R{event.non_member_fee} • {event.available_slots} slots available
                              </p>
                              <p className="text-xs text-gray-400">
                                {event.starting_tees ? (Array.isArray(event.starting_tees) ? event.starting_tees.join(', ') : event.starting_tees) : 'N/A'} • Max: {event.max_players} players/slot
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-emerald-100 text-emerald-800">
                                {event.status}
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/presentation-tee-sheet/${event.id}`)}
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                data-testid={`presentation-sheet-${event.id}`}
                              >
                                Presentation
                              </Button>
                              {event.dinner_enabled && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => fetchDinnerReport(event)}
                                  className="text-purple-600 border-purple-600 hover:bg-purple-50"
                                  title="Prize Giving Dinner report"
                                  data-testid={`dinner-report-${event.id}`}
                                >
                                  🍽️
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditEvent(event)}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteEvent(event.id, event.name)}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {events.length === 0 && (
                        <p className="text-gray-600 text-center py-4">No events found</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle>All User Bookings</CardTitle>
                <CardDescription>View and manage all user bookings across all events</CardDescription>
              </CardHeader>
              <CardContent>
                <BookingsComponent />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Credit Transactions History</CardTitle>
                  <Button onClick={exportTransactionsCSV} variant="outline" size="sm" className="text-emerald-600 border-emerald-600">
                    📥 Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Date Range Filter */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-gray-50 p-3 rounded-lg mb-4">
                  <div>
                    <label className="text-xs text-gray-500">From Date</label>
                    <Input
                      type="date"
                      value={transactionDateFilter.startDate}
                      onChange={(e) => setTransactionDateFilter(prev => ({...prev, startDate: e.target.value}))}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">To Date</label>
                    <Input
                      type="date"
                      value={transactionDateFilter.endDate}
                      onChange={(e) => setTransactionDateFilter(prev => ({...prev, endDate: e.target.value}))}
                      className="text-sm"
                    />
                  </div>
                  <div className="col-span-2 flex items-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setTransactionDateFilter({ startDate: '', endDate: '' })}
                      className="text-gray-500"
                    >
                      Clear Dates
                    </Button>
                  </div>
                </div>
                
                {/* Search Bar */}
                <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-gray-200 mb-4">
                  <svg 
                    className="w-5 h-5 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" 
                    />
                  </svg>
                  <Input
                    type="text"
                    placeholder="Search by user name, email, event, reference, type, or amount..."
                    value={transactionSearchTerm}
                    onChange={(e) => setTransactionSearchTerm(e.target.value)}
                    className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                  {transactionSearchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setTransactionSearchTerm('')}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      Clear
                    </Button>
                  )}
                </div>

                {/* Results Count */}
                {(transactionSearchTerm || transactionDateFilter.startDate || transactionDateFilter.endDate) && (
                  <div className="text-sm text-gray-600 mb-4">
                    Showing {filteredTransactions.length} of {transactions.length} transactions
                  </div>
                )}

                {loading.transactions ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  </div>
                ) : filteredTransactions.length === 0 && (transactionSearchTerm || transactionDateFilter.startDate || transactionDateFilter.endDate) ? (
                  <div className="text-center py-8 text-gray-500">
                    No transactions match your filters
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {filteredTransactions.map((transaction) => (
                      <div key={transaction.id} className="border rounded-lg p-4 bg-white shadow-sm">
                        <div className="flex justify-between items-start">
                          <div className="flex-1 space-y-2">
                            {/* User Name and Transaction Type */}
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-lg">{transaction.user_name || 'Unknown User'}</span>
                              <Badge className={
                                transaction.transaction_type === 'refund' ? 'bg-orange-100 text-orange-800' :
                                transaction.transaction_type === 'usage' ? 'bg-blue-100 text-blue-800' :
                                transaction.transaction_type === 'purchase' ? 'bg-green-100 text-green-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {transaction.transaction_type}
                              </Badge>
                            </div>
                            
                            {/* Event Information */}
                            {transaction.event_name && (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-pink-600">📍</span>
                                <span className="font-medium">{transaction.event_name}</span>
                                {transaction.booking_reference && (
                                  <span className="text-gray-500">({transaction.booking_reference})</span>
                                )}
                              </div>
                            )}
                            
                            {/* Booked By Information */}
                            {transaction.booked_by_name && (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-purple-600">👤</span>
                                <span>Booked by: {transaction.booked_by_name}</span>
                              </div>
                            )}
                            
                            {/* Payment Method / Breakdown */}
                            <div className="flex items-start gap-2 text-sm">
                              <span className="text-blue-600 mt-0.5">💳</span>
                              <div className="flex flex-col">
                                {buildTransactionBreakdown(transaction).map((line, i) => (
                                  <span key={i} data-testid={`txn-breakdown-${transaction.id}-${i}`}>{line}</span>
                                ))}
                                {transaction.booking_admin_recovered && (
                                  <span className="mt-1">
                                    <Badge className="bg-amber-100 text-amber-800 text-xs">admin recovered</Badge>
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Transaction Details/Cancellation Info */}
                            {transaction.transaction_type === 'refund' && transaction.cancelled_by_name && (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-red-600">✗</span>
                                <span>Cancelled by: {transaction.cancelled_by_name}</span>
                                {transaction.includes_cart_refund && (
                                  <span>• Refund: Credits (incl. cart)</span>
                                )}
                              </div>
                            )}
                            
                            {/* Timestamp and Email */}
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>🕐</span>
                              <span>{formatDate(transaction.created_at)}</span>
                              {transaction.user_email && (
                                <>
                                  <span>•</span>
                                  <span>{transaction.user_email}</span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Amount */}
                          <div className={`text-xl font-bold ml-4 ${transaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {transaction.amount >= 0 ? '+' : ''}R{Math.abs(transaction.amount).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {transactions.length === 0 && (
                      <p className="text-gray-600 text-center py-8">No transactions found</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Course Database</CardTitle>
                  <Button 
                    onClick={() => {
                      setSelectedCourse(null);
                      setCourseForm({ name: '', location: '', province: '', tees: [] });
                      setCourseModal(true);
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    + Add Course
                  </Button>
                </div>
                <p className="text-sm text-gray-600">
                  Manage courses with tee configurations for playing handicap calculations.
                </p>
              </CardHeader>
              <CardContent>
                {courses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-lg mb-2">No courses added yet</p>
                    <p className="text-sm">Add your first course to enable playing handicap calculations on tee sheets.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {courses.map(course => (
                      <div key={course.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-lg">{course.name}</h3>
                            {(course.location || course.province) && (
                              <p className="text-sm text-gray-600">
                                📍 {[course.location, course.province].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditCourse(course)}
                            >
                              Edit
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 border-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteCourse(course.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                        
                        {/* Tee Configurations */}
                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">Tee Configurations:</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {course.tees?.map((tee, idx) => (
                              <div key={idx} className="bg-gray-100 rounded p-2 text-sm">
                                <span className="font-medium capitalize">
                                  {tee.tee_name === 'mens' ? "Men's" : 
                                   tee.tee_name === 'ladies' ? "Ladies" :
                                   tee.tee_name === 'senior_01' ? "Senior 01" :
                                   tee.tee_name === 'senior_02' ? "Senior 02" :
                                   tee.tee_name}
                                </span>
                                <div className="text-gray-600">
                                  CR: {tee.course_rating} • SR: {tee.slope_rating} • Par: {tee.par}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Admin Logs Tab */}
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Admin Activity Logs</CardTitle>
                  <Button onClick={exportAdminLogsCSV} variant="outline" size="sm" className="text-emerald-600 border-emerald-600">
                    📥 Export CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Date Range Filter */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-gray-50 p-3 rounded-lg mb-4">
                  <div>
                    <label className="text-xs text-gray-500">From Date</label>
                    <Input
                      type="date"
                      value={adminLogDateFilter.startDate}
                      onChange={(e) => setAdminLogDateFilter(prev => ({...prev, startDate: e.target.value}))}
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">To Date</label>
                    <Input
                      type="date"
                      value={adminLogDateFilter.endDate}
                      onChange={(e) => setAdminLogDateFilter(prev => ({...prev, endDate: e.target.value}))}
                      className="text-sm"
                    />
                  </div>
                  <div className="col-span-2 flex items-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setAdminLogDateFilter({ startDate: '', endDate: '' })}
                      className="text-gray-500"
                    >
                      Clear Dates
                    </Button>
                  </div>
                </div>
                
                {/* Results Count */}
                {(adminLogDateFilter.startDate || adminLogDateFilter.endDate) && (
                  <div className="text-sm text-gray-600 mb-4">
                    Showing {filteredAdminLogs.length} of {adminLogs.length} logs
                  </div>
                )}
                
                {loading.logs ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {Array.isArray(adminLogDateFilter.startDate || adminLogDateFilter.endDate ? filteredAdminLogs : adminLogs) && (adminLogDateFilter.startDate || adminLogDateFilter.endDate ? filteredAdminLogs : adminLogs).filter(log => log && typeof log === 'object').map((log, index) => (
                    <div key={log.id || log._id || index} className="p-4 border rounded-lg bg-white shadow-sm">
                      <div className="space-y-2">
                        {/* Action Type Badge and Admin Name */}
                        <div className="flex items-center gap-2">
                          <Badge className={
                            log.action === 'free_rounds_adjustment' ? 'bg-purple-100 text-purple-800' :
                            log.action === 'import_users' ? 'bg-blue-100 text-blue-800' :
                            log.action === 'credits_adjustment' ? 'bg-green-100 text-green-800' :
                            log.action === 'user_profile_update' ? 'bg-indigo-100 text-indigo-800' :
                            log.action === 'password_reset' ? 'bg-orange-100 text-orange-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {(log.action || 'unknown').replace(/_/g, ' ')}
                          </Badge>
                          <span className="font-medium">by {log.admin_name || 'Admin'}</span>
                        </div>
                        
                        {/* Target User Information */}
                        {log.target_user_name && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-purple-600">👤</span>
                            <span>Target: <span className="font-medium">{log.target_user_name}</span></span>
                            {log.target_user_email && (
                              <span className="text-gray-500">({log.target_user_email})</span>
                            )}
                            {log.target_user_membership && log.target_user_membership !== 'non-member' && (
                              <Badge className="bg-blue-50 text-blue-700 text-xs">
                                {log.target_user_membership}
                              </Badge>
                            )}
                            {log.target_user_membership === 'non-member' && (
                              <Badge className="bg-gray-50 text-gray-700 text-xs">
                                non-member
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Details */}
                        {(log.details || log.details_formatted) && (
                          <div className="text-sm text-gray-700">
                            {Array.isArray(log.details_formatted) ? (
                              <ul className="list-disc list-inside space-y-0.5">
                                {log.details_formatted.map((line, i) => (
                                  <li key={i} data-testid={`admin-log-change-${index}-${i}`}>{line}</li>
                                ))}
                              </ul>
                            ) : typeof log.details === 'string'
                              ? log.details
                              : typeof log.details_formatted === 'string'
                                ? log.details_formatted
                                : log.details
                                  ? JSON.stringify(log.details)
                                  : JSON.stringify(log.details_formatted)
                            }
                          </div>
                        )}
                        
                        {/* Timestamp and Admin Email */}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>🕐</span>
                          <span>{formatDateTime(log.timestamp)}</span>
                          {log.admin_email && (
                            <>
                              <span>•</span>
                              <span>{log.admin_email}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!Array.isArray(adminLogs) || adminLogs.length === 0) && (
                    <p className="text-gray-600 text-center py-8">No admin logs found</p>
                  )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        {/* Enhanced User Management Modals */}

        {/* User Transactions Modal - shows per-user credit history with running balance */}
        <Dialog open={userTxnModal} onOpenChange={(open) => {
          setUserTxnModal(open);
          if (!open) {
            setUserTxnTarget(null);
            setUserTxnList([]);
            setUserTxnFromDate('');
            setUserTxnToDate('');
          }
        }}>
          <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-transactions-modal">
            <DialogHeader>
              <DialogTitle>
                Transactions
                {userTxnTarget && (
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    — {userTxnTarget.first_name} {userTxnTarget.last_name} ({userTxnTarget.email || userTxnTarget.username})
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>

            {/* Date range filter */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-2 mb-4 items-end">
              <div>
                <Label htmlFor="user-txn-from" className="text-xs text-gray-600">From date</Label>
                <Input
                  id="user-txn-from"
                  type="date"
                  value={userTxnFromDate}
                  onChange={(e) => setUserTxnFromDate(e.target.value)}
                  data-testid="user-txn-from-date"
                />
              </div>
              <div>
                <Label htmlFor="user-txn-to" className="text-xs text-gray-600">To date</Label>
                <Input
                  id="user-txn-to"
                  type="date"
                  value={userTxnToDate}
                  onChange={(e) => setUserTxnToDate(e.target.value)}
                  data-testid="user-txn-to-date"
                />
              </div>
              <Button
                size="sm"
                onClick={() => userTxnTarget && fetchUserTransactions(userTxnTarget, userTxnFromDate, userTxnToDate)}
                data-testid="user-txn-apply-dates"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Apply
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setUserTxnFromDate('');
                  setUserTxnToDate('');
                  if (userTxnTarget) fetchUserTransactions(userTxnTarget);
                }}
                data-testid="user-txn-clear-dates"
              >
                Clear
              </Button>
            </div>

            {userTxnLoading ? (
              <div className="text-center py-12 text-gray-500">Loading transactions…</div>
            ) : (
              <>
                {/* Summary header */}
                {(() => {
                  // Topped up = purchases + positive admin adjustments
                  const toppedUp = userTxnList
                    .filter(t => t.transaction_type === 'purchase'
                      || (t.transaction_type === 'admin_adjustment' && Number(t.amount || 0) > 0))
                    .reduce((s, t) => s + Number(t.amount || 0), 0);
                  // Spent = usage + negative admin adjustments
                  const spent = userTxnList
                    .filter(t => t.transaction_type === 'usage'
                      || (t.transaction_type === 'admin_adjustment' && Number(t.amount || 0) < 0))
                    .reduce((s, t) => s + Math.abs(Number(t.amount || 0)), 0);
                  const refunded = userTxnList
                    .filter(t => t.transaction_type === 'refund')
                    .reduce((s, t) => s + Number(t.amount || 0), 0);
                  const currentBalance = Number(userTxnTarget?.credits_balance || 0);
                  return (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div className="bg-green-50 border border-green-100 rounded p-3">
                        <div className="text-xs text-green-700">Topped up</div>
                        <div className="text-lg font-semibold text-green-800" data-testid="user-txn-total-topped-up">R{toppedUp.toFixed(2)}</div>
                      </div>
                      <div className="bg-blue-50 border border-blue-100 rounded p-3">
                        <div className="text-xs text-blue-700">Spent</div>
                        <div className="text-lg font-semibold text-blue-800" data-testid="user-txn-total-spent">R{spent.toFixed(2)}</div>
                      </div>
                      <div className="bg-orange-50 border border-orange-100 rounded p-3">
                        <div className="text-xs text-orange-700">Refunded</div>
                        <div className="text-lg font-semibold text-orange-800" data-testid="user-txn-total-refunded">R{refunded.toFixed(2)}</div>
                      </div>
                      <div className="bg-emerald-50 border border-emerald-100 rounded p-3">
                        <div className="text-xs text-emerald-700">Current balance</div>
                        <div className="text-lg font-semibold text-emerald-800" data-testid="user-txn-current-balance">R{currentBalance.toFixed(2)}</div>
                      </div>
                    </div>
                  );
                })()}

                {userTxnList.length === 0 ? (
                  <div className="text-center py-12 text-gray-500" data-testid="user-txn-empty">No transactions for this user.</div>
                ) : (
                  <div className="space-y-2 max-h-[55vh] overflow-y-auto">
                    {userTxnList.map((t) => (
                      <div key={t.id} className="border rounded p-3 bg-white" data-testid={`user-txn-row-${t.id}`}>
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge className={
                                t.transaction_type === 'refund' ? 'bg-orange-100 text-orange-800' :
                                t.transaction_type === 'usage' ? 'bg-blue-100 text-blue-800' :
                                t.transaction_type === 'purchase' ? 'bg-green-100 text-green-800' :
                                t.transaction_type === 'admin_adjustment' ? 'bg-emerald-100 text-emerald-800' :
                                t.transaction_type === 'admin_recovery' ? 'bg-amber-100 text-amber-800' :
                                t.transaction_type === 'free_round_usage' ? 'bg-teal-100 text-teal-800' :
                                t.transaction_type === 'free_round_adjustment' ? 'bg-cyan-100 text-cyan-800' :
                                t.transaction_type === 'free_round_monthly' ? 'bg-sky-100 text-sky-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {String(t.transaction_type || '').replace(/_/g, ' ')}
                              </Badge>
                              {t.event_name && (
                                <span className="text-sm text-gray-700">
                                  {t.event_name}
                                  {t.booking_reference ? ` (${t.booking_reference})` : ''}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-600 flex flex-col">
                              {buildTransactionBreakdown(t).map((line, i) => (
                                <span key={i}>{line}</span>
                              ))}
                              {t.booking_admin_recovered && (
                                <span className="mt-1">
                                  <Badge className="bg-amber-100 text-amber-800 text-xs">admin recovered</Badge>
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">{formatDate(t.created_at)}</div>
                          </div>
                          <div className="text-right">
                            {String(t.transaction_type || '').startsWith('free_round_') ? (
                              <>
                                <div className="font-semibold text-emerald-700">
                                  {Number(t.free_rounds_delta || 0) > 0 ? '+' : ''}
                                  {Number(t.free_rounds_delta || 0)} free round
                                </div>
                                <div className="text-xs text-gray-500">no credit impact</div>
                              </>
                            ) : (
                              <>
                                <div className={`font-semibold ${t.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {t.amount >= 0 ? '+' : ''}R{Math.abs(Number(t.amount || 0)).toFixed(2)}
                                </div>
                                <div className="text-xs text-gray-500">balance: R{Number(t.running_balance || 0).toFixed(2)}</div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setUserTxnModal(false)} data-testid="user-txn-close">Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal - Portal to avoid tab navigation conflicts */}
        <Dialog open={editUserModal} onOpenChange={(open) => {
          setEditUserModal(open);
          if (!open) {
            setSelectedUser(null);
          }
        }}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Edit User Profile</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={userEditForm.first_name}
                    onChange={(e) => setUserEditForm({...userEditForm, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={userEditForm.last_name}
                    onChange={(e) => setUserEditForm({...userEditForm, last_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Phone</Label>
                <Input
                  value={userEditForm.phone}
                  onChange={(e) => setUserEditForm({...userEditForm, phone: e.target.value})}
                />
              </div>
              
              <div>
                <Label>SAGA Number</Label>
                <Input
                  value={userEditForm.saga_number}
                  onChange={(e) => setUserEditForm({...userEditForm, saga_number: e.target.value})}
                />
              </div>
              
              <div>
                <Label>DH Number</Label>
                <div className="space-y-3">
                  <Input
                    value={userEditForm.dh_number || ''}
                    onChange={(e) => setUserEditForm({...userEditForm, dh_number: e.target.value})}
                    placeholder="e.g., 4800000268"
                    pattern="48\d{8}"
                  />
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="auto-generate-dh"
                      checked={userEditForm.auto_generate_dh || false}
                      onChange={(e) => {
                        setUserEditForm({
                          ...userEditForm, 
                          auto_generate_dh: e.target.checked,
                          dh_number: e.target.checked ? '' : userEditForm.dh_number
                        });
                      }}
                    />
                    <Label htmlFor="auto-generate-dh" className="text-sm">
                      Auto-generate DH Number
                    </Label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Format: 48 followed by 8 digits (e.g., 4800000268)
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Membership Tier</Label>
                  <Select
                    value={userEditForm.membership_tier}
                    onValueChange={(value) => setUserEditForm({...userEditForm, membership_tier: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non-member">Non-Member</SelectItem>
                      <SelectItem value="green">Green</SelectItem>
                      <SelectItem value="silver">Silver</SelectItem>
                      <SelectItem value="gold">Gold</SelectItem>
                      <SelectItem value="special">🎯 Special (Pre-Purchase)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>User Role</Label>
                  <Select
                    value={userEditForm.role === 'user' ? 'regular' : userEditForm.role}
                    onValueChange={(value) => setUserEditForm({...userEditForm, role: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular User</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>DH Handicap (DHG)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={userEditForm.dh_handicap}
                    onChange={(e) => setUserEditForm({...userEditForm, dh_handicap: e.target.value})}
                    placeholder="e.g. 18.5"
                  />
                  <p className="text-xs text-gray-500 mt-1">DuckHook handicap - supports decimals</p>
                </div>
                <div>
                  <Label>HNA Handicap (SAGA)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={userEditForm.hna_handicap}
                    onChange={(e) => setUserEditForm({...userEditForm, hna_handicap: e.target.value})}
                    placeholder="e.g. 15.2"
                  />
                  <p className="text-xs text-gray-500 mt-1">HNA/SAGA official handicap</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>DH 95% (Playing HCP)</Label>
                  <Input
                    type="text"
                    value={selectedUser?.dh_95_percent || 'N/A'}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
                </div>
                <div>
                  <Label>HNA 95% (Playing HCP)</Label>
                  <Input
                    type="text"
                    value={selectedUser?.hna_95_percent || 'N/A'}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated</p>
                </div>
              </div>
              
              {/* Player Status & Tee Settings */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium text-gray-800">Player Status & Tee Settings</h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Gender</Label>
                    <Select
                      value={userEditForm.gender}
                      onValueChange={(value) => setUserEditForm({...userEditForm, gender: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Default Tee</Label>
                    <Select
                      value={userEditForm.default_tee}
                      onValueChange={(value) => setUserEditForm({...userEditForm, default_tee: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select default tee" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="mens">Men's</SelectItem>
                        <SelectItem value="ladies">Ladies</SelectItem>
                        <SelectItem value="senior_01">Senior 01</SelectItem>
                        <SelectItem value="senior_02">Senior 02</SelectItem>
                        <SelectItem value="junior">Junior</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2 p-3 border rounded bg-yellow-50">
                    <input
                      type="checkbox"
                      id="is_new_player"
                      checked={userEditForm.is_new_player}
                      onChange={(e) => setUserEditForm({...userEditForm, is_new_player: e.target.checked})}
                    />
                    <Label htmlFor="is_new_player" className="text-yellow-800">
                      New Player (80% × 95% of playing HCP)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded bg-orange-50">
                    <input
                      type="checkbox"
                      id="has_hna"
                      checked={userEditForm.has_hna}
                      onChange={(e) => setUserEditForm({...userEditForm, has_hna: e.target.checked})}
                    />
                    <Label htmlFor="has_hna" className="text-orange-800">
                      Has HNA Handicap
                    </Label>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600 bg-gray-100 p-3 rounded">
                  <strong>Playing Handicap Rules:</strong>
                  <ul className="list-disc ml-5 mt-1">
                    <li><strong>Regular HNA:</strong> Playing HCP × 95%</li>
                    <li><strong>New + HNA:</strong> Playing HCP × 80% × 95%</li>
                    <li><strong>New + Non-HNA:</strong> Plays off 0</li>
                    <li><strong>Non-HNA:</strong> DHG × 75% × 95%</li>
                  </ul>
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Handicap Last Calculated</Label>
                  <Input
                    type="text"
                    value={selectedUser?.handicap_last_calculated 
                      ? parseUTCDate(selectedUser.handicap_last_calculated).toLocaleDateString() 
                      : 'Never calculated'
                    }
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">Updated via import or manual entry</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={userEditForm.email_verified}
                  onChange={(e) => setUserEditForm({...userEditForm, email_verified: e.target.checked})}
                />
                <Label>Email Verified</Label>
              </div>
              
              {/* Credit Management Section */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium text-gray-800">Credit & Free Round Management</h4>
                
                {/* Current Balance Display */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-gray-50 rounded">
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Current Credits</Label>
                    <p className="text-lg font-semibold text-green-600">R{(selectedUser?.credits_balance || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Free Rounds</Label>
                    <p className="text-lg font-semibold text-blue-600">{selectedUser?.free_rounds || 0}</p>
                  </div>
                </div>

                {/* Add Credits */}
                <div className="p-3 border border-green-200 rounded bg-green-50">
                  <h5 className="font-medium text-green-800 mb-2">Add Credits</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Amount (R)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={userEditForm.add_credits_amount || ''}
                        onChange={(e) => setUserEditForm({...userEditForm, add_credits_amount: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Input
                        value={userEditForm.add_credits_reason || ''}
                        onChange={(e) => setUserEditForm({...userEditForm, add_credits_reason: e.target.value})}
                        placeholder="Reason for adding credits"
                      />
                    </div>
                  </div>
                </div>

                {/* Remove Credits */}
                <div className="p-3 border border-red-200 rounded bg-red-50">
                  <h5 className="font-medium text-red-800 mb-2">Remove Credits</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Amount (R)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={userEditForm.remove_credits_amount || ''}
                        onChange={(e) => setUserEditForm({...userEditForm, remove_credits_amount: e.target.value})}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Input
                        value={userEditForm.remove_credits_reason || ''}
                        onChange={(e) => setUserEditForm({...userEditForm, remove_credits_reason: e.target.value})}
                        placeholder="Reason for removing credits"
                      />
                    </div>
                  </div>
                </div>

                {/* Free Rounds Management */}
                <div className="p-3 border border-blue-200 rounded bg-blue-50">
                  <h5 className="font-medium text-blue-800 mb-2">Adjust Free Rounds</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Set Free Rounds</Label>
                      <Input
                        type="number"
                        min="0"
                        value={userEditForm.free_rounds_new || selectedUser?.free_rounds || ''}
                        onChange={(e) => setUserEditForm({...userEditForm, free_rounds_new: e.target.value})}
                        placeholder="Number of free rounds"
                      />
                    </div>
                    <div>
                      <Label>Reason</Label>
                      <Input
                        value={userEditForm.free_rounds_reason || ''}
                        onChange={(e) => setUserEditForm({...userEditForm, free_rounds_reason: e.target.value})}
                        placeholder="Reason for adjustment"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleUpdateUser} 
                  className="flex-1"
                  disabled={loading.updateUser}
                >
                  {loading.updateUser ? 'Updating...' : 'Update User'}
                </Button>
                <Button variant="outline" onClick={() => setEditUserModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Operations Modal */}
        <Dialog open={bulkOperationModal} onOpenChange={setBulkOperationModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Bulk Operations</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Operation</Label>
                <Select
                  value={bulkOperationForm.operation}
                  onValueChange={(value) => setBulkOperationForm({...bulkOperationForm, operation: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="credits_add">Add Credits</SelectItem>
                    <SelectItem value="credits_deduct">Deduct Credits</SelectItem>
                    <SelectItem value="free_rounds_set">Set Free Rounds</SelectItem>
                    <SelectItem value="membership_tier">Change Membership Tier</SelectItem>
                    <SelectItem value="delete_users">🗑️ Delete Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Only show Value field for operations that need it */}
              {bulkOperationForm.operation !== 'delete_users' && (
                <div>
                  <Label>Value</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={bulkOperationForm.value}
                    onChange={(e) => setBulkOperationForm({...bulkOperationForm, value: parseFloat(e.target.value)})}
                  />
                </div>
              )}
              
              <div className="text-sm text-gray-600">
                This operation will be applied to {selectedUsers.length} selected user(s).
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button onClick={handleBulkOperation} className="flex-1">
                  Apply Operation
                </Button>
                <Button variant="outline" onClick={() => setBulkOperationModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Password Reset Modal */}
        <Dialog open={passwordResetModal} onOpenChange={setPasswordResetModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reset User Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={passwordResetForm.new_password}
                  onChange={(e) => setPasswordResetForm({...passwordResetForm, new_password: e.target.value})}
                  placeholder="Enter new password"
                />
              </div>
              
              <div>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={passwordResetForm.confirm_password}
                  onChange={(e) => setPasswordResetForm({...passwordResetForm, confirm_password: e.target.value})}
                  placeholder="Confirm new password"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button onClick={handleResetPassword} className="flex-1">
                  Reset Password
                </Button>
                <Button variant="outline" onClick={() => setPasswordResetModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Import Users Modal */}
        <Dialog open={importModal} onOpenChange={setImportModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Import Users</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Select File</Label>
                <Input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: CSV, Excel (.xlsx, .xls)
                </p>
              </div>
              
              <div className="p-3 bg-blue-50 rounded border">
                <h4 className="font-medium text-blue-800 mb-2">Required Columns:</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• email (required)</li>
                  <li>• first_name (required)</li>
                  <li>• last_name (required)</li>
                  <li>• phone (required, format: 27xxxxxxxxx)</li>
                  <li>• saga_number (required, format: 27xxxxxxxx)</li>
                  <li>• membership_tier (optional: non-member, green, silver, gold, special)</li>
                </ul>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleImportUsers} 
                  className="flex-1"
                  disabled={!importFile}
                >
                  Import Users
                </Button>
                <Button variant="outline" onClick={() => setImportModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Export Users Modal */}
        <Dialog open={exportModal} onOpenChange={setExportModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Export Users</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Export all user data including profiles, membership information, and account details.
              </p>
              
              <div className="space-y-3">
                <h4 className="font-medium">Select Export Format:</h4>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => handleExportUsers('csv')} 
                    className="flex-1"
                  >
                    Export as CSV
                  </Button>
                  <Button 
                    onClick={() => handleExportUsers('xlsx')} 
                    variant="outline"
                    className="flex-1"
                  >
                    Export as Excel
                  </Button>
                </div>
              </div>
              
              <div className="p-3 bg-gray-50 rounded border">
                <h4 className="font-medium text-gray-800 mb-2">Exported Data:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• User profiles (name, email, phone)</li>
                  <li>• SAGA and DH numbers (48xxxxxxxx format)</li>
                  <li>• Membership tiers and status</li>
                  <li>• Credits and free rounds balances</li>
                  <li>• Account creation dates</li>
                </ul>
              </div>
              
              <Button variant="outline" onClick={() => setExportModal(false)} className="w-full">
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Event Edit Modal */}
        <Dialog open={editEventModal} onOpenChange={setEditEventModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Event</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Event Name</Label>
                <Input
                  value={newEvent.name}
                  onChange={(e) => setNewEvent({...newEvent, name: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={newEvent.start_time}
                  onChange={(e) => setNewEvent({...newEvent, start_time: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={newEvent.end_time}
                  onChange={(e) => setNewEvent({...newEvent, end_time: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Member Fee (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEvent.member_fee}
                  onChange={(e) => setNewEvent({...newEvent, member_fee: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Non-Member Fee (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEvent.non_member_fee}
                  onChange={(e) => setNewEvent({...newEvent, non_member_fee: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Cart Fee (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newEvent.cart_fee}
                  onChange={(e) => setNewEvent({...newEvent, cart_fee: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Max Players per Slot</Label>
                <Input
                  type="number"
                  min="1"
                  max="4"
                  value={newEvent.max_players}
                  onChange={(e) => setNewEvent({...newEvent, max_players: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <Label>Starting Tees (comma-separated)</Label>
                <Input
                  value={newEvent.starting_tees}
                  onChange={(e) => setNewEvent({...newEvent, starting_tees: e.target.value})}
                  placeholder="e.g., 1st, 10th"
                  required
                />
              </div>
              
              <div>
                <Label>Tee Time Interval (minutes)</Label>
                <Select
                  value={newEvent.tee_time_interval?.toString() || '10'}
                  onValueChange={(value) => setNewEvent({...newEvent, tee_time_interval: parseInt(value)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[6, 8, 10, 12, 15, 20, 30].map(num => (
                      <SelectItem key={num} value={num.toString()}>{num} min</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Total Players</Label>
                <Input
                  type="number"
                  min="1"
                  value={newEvent.number_of_players}
                  onChange={(e) => setNewEvent({...newEvent, number_of_players: e.target.value})}
                  required
                />
              </div>
              
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  className="min-h-[80px]"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Image URL (optional)</Label>
                <Input
                  type="url"
                  value={newEvent.image_url || ''}
                  onChange={(e) => setNewEvent({...newEvent, image_url: e.target.value})}
                  placeholder="https://example.com/event-image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">Enter a full URL to an image for this event</p>
              </div>
              
              <div className="col-span-2 flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-cart-required"
                    checked={newEvent.cart_required}
                    onChange={(e) => setNewEvent({...newEvent, cart_required: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="edit-cart-required">Cart Required</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-free-rounds-allowed"
                    checked={newEvent.free_rounds_allowed}
                    onChange={(e) => setNewEvent({...newEvent, free_rounds_allowed: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="edit-free-rounds-allowed">Allow Free Rounds</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-bookings-open"
                    checked={newEvent.bookings_open}
                    onChange={(e) => setNewEvent({...newEvent, bookings_open: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="edit-bookings-open">Bookings Open</Label>
                </div>
              </div>
              
              {/* Course Selection for Playing Handicap in Edit Modal */}
              <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <Label htmlFor="edit-course-select" className="font-semibold text-blue-900">Course (for Playing Handicap)</Label>
                <Select
                  value={newEvent.course_id || "none"}
                  onValueChange={(value) => setNewEvent({...newEvent, course_id: value === "none" ? "" : value})}
                >
                  <SelectTrigger className="mt-2" data-testid="edit-course-select">
                    <SelectValue placeholder="Select a course (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No course selected</SelectItem>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.name} {course.location && `(${course.location})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-blue-700 mt-2">
                  Linking a course enables playing handicap calculations on the tee sheet based on course rating and slope.
                </p>
              </div>
              
              {/* Mhala Hole-in-One Competition Section in Edit Modal */}
              <div className="col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-testid="edit-mhala-section">
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id="edit-mhala-enabled"
                    checked={newEvent.mhala_hole_in_one_enabled}
                    onChange={(e) => setNewEvent({...newEvent, mhala_hole_in_one_enabled: e.target.checked})}
                    className="rounded"
                    data-testid="edit-mhala-enabled-checkbox"
                  />
                  <Label htmlFor="edit-mhala-enabled" className="font-semibold text-yellow-800">
                    🏆 Mhala Hole-in-One Competition
                  </Label>
                </div>
                {newEvent.mhala_hole_in_one_enabled && (
                  <div className="ml-6">
                    <Label htmlFor="edit-mhala-fee" className="text-sm text-yellow-700">Fee (R)</Label>
                    <Input
                      id="edit-mhala-fee"
                      type="number"
                      value={newEvent.mhala_hole_in_one_fee}
                      onChange={(e) => setNewEvent({...newEvent, mhala_hole_in_one_fee: parseFloat(e.target.value) || 0})}
                      className="w-32 mt-1"
                      min="0"
                      step="0.01"
                      data-testid="edit-mhala-fee-input"
                    />
                  </div>
                )}
              </div>
              
              {/* Shotgun Start Section in Edit Modal */}
              <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="edit-shotgun-section">
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="checkbox"
                    id="edit-shotgun-start"
                    checked={newEvent.shotgun_start}
                    onChange={(e) => setNewEvent({...newEvent, shotgun_start: e.target.checked})}
                    className="rounded"
                    data-testid="edit-shotgun-start-checkbox"
                  />
                  <Label htmlFor="edit-shotgun-start" className="font-semibold text-blue-800">
                    🎯 Shotgun Start (All groups start same time, different holes)
                  </Label>
                </div>
                {newEvent.shotgun_start && (
                  <div className="ml-6 grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-shotgun-starting-hole" className="text-sm text-blue-700">Starting Hole</Label>
                      <Select
                        value={newEvent.shotgun_starting_hole?.toString() || '1'}
                        onValueChange={(value) => setNewEvent({...newEvent, shotgun_starting_hole: parseInt(value)})}
                      >
                        <SelectTrigger className="mt-1" data-testid="edit-shotgun-starting-hole">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">Hole 1</SelectItem>
                          <SelectItem value="10">Hole 10</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-shotgun-num-holes" className="text-sm text-blue-700">Number of Holes</Label>
                      <Select
                        value={newEvent.shotgun_num_holes?.toString() || '18'}
                        onValueChange={(value) => setNewEvent({...newEvent, shotgun_num_holes: parseInt(value)})}
                      >
                        <SelectTrigger className="mt-1" data-testid="edit-shotgun-num-holes">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="9">9 Holes</SelectItem>
                          <SelectItem value="18">18 Holes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="col-span-2 text-xs text-blue-600 mt-1">
                      ⚠️ Changing shotgun settings will regenerate tee times. Existing bookings will be preserved but may become orphaned if their hole no longer exists.
                    </p>
                  </div>
                )}
              </div>
              
              <div className="col-span-2 flex gap-3 pt-4">
                <Button onClick={handleUpdateEvent} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Update Event
                </Button>
                <Button variant="outline" onClick={() => setEditEventModal(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Free Rounds Report Modal */}
        <Dialog open={freeRoundsReportModal} onOpenChange={setFreeRoundsReportModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>📊 Free Rounds Usage Report</DialogTitle>
              <DialogDescription>
                Generate detailed reports on free round allocations and usage patterns
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Allocation Status Banner */}
              {loadingAllocationStatus && (
                <div className="p-4 rounded-lg border bg-gray-50 border-gray-200 text-center">
                  <span className="text-gray-600">Loading allocation status...</span>
                </div>
              )}
              {!loadingAllocationStatus && allocationStatus && (
                <div className={`p-4 rounded-lg border ${allocationStatus.allocation_done_this_month ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-sm mb-2">
                        {allocationStatus.allocation_done_this_month ? '✅ Monthly Allocation Complete' : '⏳ Monthly Allocation Pending'}
                      </h4>
                      <div className="text-xs text-gray-600 space-y-1">
                        <p>Current Month: {allocationStatus.current_month}/{allocationStatus.current_year}</p>
                        <p>Users Allocated This Month: {allocationStatus.users_allocated_this_month}</p>
                        {allocationStatus.last_allocation && (
                          <p>Last Allocation: {new Date(allocationStatus.last_allocation.date).toLocaleDateString()} ({allocationStatus.last_allocation.type})</p>
                        )}
                        <p>Next Scheduled: {allocationStatus.next_scheduled_run !== 'Not scheduled' ? new Date(allocationStatus.next_scheduled_run).toLocaleString() : 'Not scheduled'}</p>
                        <p className="text-gray-500">Auto-allocation runs on the 15th of each month</p>
                      </div>
                    </div>
                    <Button
                      onClick={handleManualAllocation}
                      disabled={allocatingRounds}
                      variant={allocationStatus.allocation_done_this_month ? "outline" : "default"}
                      className={allocationStatus.allocation_done_this_month ? "" : "bg-emerald-600 hover:bg-emerald-700"}
                    >
                      {allocatingRounds ? '⏳ Allocating...' : '🎁 Allocate Now'}
                    </Button>
                  </div>
                  {allocationStatus.allocation_done_this_month && (
                    <p className="text-xs text-amber-600 mt-2">⚠️ Allocation already done this month. Running again will skip already-allocated users.</p>
                  )}
                </div>
              )}

              {/* Date Range Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    type="date"
                    id="start-date"
                    value={reportDateRange.startDate}
                    onChange={(e) => setReportDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    type="date"
                    id="end-date"
                    value={reportDateRange.endDate}
                    onChange={(e) => setReportDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  />
                </div>
              </div>

              {/* Generate Report Button */}
              <Button 
                onClick={handleGenerateFreeRoundsReport} 
                disabled={loading.freeRoundsReport}
                className="w-full"
              >
                {loading.freeRoundsReport ? 'Generating Report...' : 'Generate Report'}
              </Button>

              {/* Report Results */}
              {freeRoundsReport && (
                <div className="border rounded-lg p-6 bg-gray-50">
                  <h3 className="font-semibold text-lg mb-2">Report Results</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Period: {freeRoundsReport.report_period?.start_date} to {freeRoundsReport.report_period?.end_date}
                  </p>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-4 rounded border">
                      <div className="text-2xl font-bold text-green-600">{freeRoundsReport.summary?.total_allocated || 0}</div>
                      <div className="text-sm text-gray-600">Allocated (in period)</div>
                    </div>
                    <div className="bg-white p-4 rounded border">
                      <div className="text-2xl font-bold text-blue-600">{freeRoundsReport.summary?.total_used || 0}</div>
                      <div className="text-sm text-gray-600">Used (in period)</div>
                    </div>
                    <div className="bg-white p-4 rounded border">
                      <div className="text-2xl font-bold text-orange-600">{freeRoundsReport.summary?.total_remaining || 0}</div>
                      <div className="text-sm text-gray-600">Current Balance</div>
                    </div>
                    <div className="bg-white p-4 rounded border">
                      <div className="text-2xl font-bold text-purple-600">{freeRoundsReport.summary?.unique_users || 0}</div>
                      <div className="text-sm text-gray-600">Active Users</div>
                    </div>
                  </div>

                  {/* Detailed User Data */}
                  {freeRoundsReport.user_details && freeRoundsReport.user_details.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">User Details</h4>
                      
                      {/* Search Bar */}
                      <div className="mb-4">
                        <Input
                          type="text"
                          placeholder="🔍 Search by name or email..."
                          value={freeRoundsSearch}
                          onChange={(e) => setFreeRoundsSearch(e.target.value)}
                          className="w-full"
                        />
                      </div>
                      
                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white sticky top-0">
                            <tr>
                              <th className="text-left p-2 border">User</th>
                              <th className="text-left p-2 border">Membership</th>
                              <th className="text-left p-2 border">Allocated (period)</th>
                              <th className="text-left p-2 border">Used (period)</th>
                              <th className="text-left p-2 border">Current Balance</th>
                              <th className="text-left p-2 border">Last Used (period)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {freeRoundsReport.user_details
                              .filter(user => {
                                if (!freeRoundsSearch) return true;
                                const search = freeRoundsSearch.toLowerCase();
                                return (
                                  user.name?.toLowerCase().includes(search) ||
                                  user.email?.toLowerCase().includes(search)
                                );
                              })
                              .map((user, index) => (
                              <tr key={index} className="hover:bg-gray-100">
                                <td className="p-2 border">{user.name} ({user.email})</td>
                                <td className="p-2 border">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    user.membership_tier === 'gold' ? 'bg-yellow-100 text-yellow-800' :
                                    user.membership_tier === 'silver' ? 'bg-gray-100 text-gray-800' :
                                    user.membership_tier === 'green' ? 'bg-green-100 text-green-800' :
                                    user.membership_tier === 'special' ? 'bg-purple-100 text-purple-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {user.membership_tier === 'special' ? '🎯 Special' : user.membership_tier || 'Non-Member'}
                                  </span>
                                </td>
                                <td className="p-2 border text-center">{user.allocated || 0}</td>
                                <td className="p-2 border text-center">{user.used || 0}</td>
                                <td className="p-2 border text-center">{user.remaining || 0}</td>
                                <td className="p-2 border">{user.last_used || 'Never'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Allocations History with Download */}
                  {freeRoundsReport.allocations && freeRoundsReport.allocations.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold">📋 Allocation History ({freeRoundsReport.allocations.length} records)</h4>
                        <Button 
                          onClick={downloadFreeRoundsCSV}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          📥 Download CSV
                        </Button>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white sticky top-0">
                            <tr>
                              <th className="text-left p-2 border">User</th>
                              <th className="text-left p-2 border">Membership</th>
                              <th className="text-center p-2 border">Rounds</th>
                              <th className="text-left p-2 border">Date</th>
                              <th className="text-left p-2 border">Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {freeRoundsReport.allocations.map((alloc, index) => (
                              <tr key={index} className="hover:bg-white">
                                <td className="p-2 border">{alloc.user_name || 'Unknown'}</td>
                                <td className="p-2 border">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    alloc.membership_tier === 'gold' ? 'bg-yellow-100 text-yellow-800' :
                                    alloc.membership_tier === 'silver' ? 'bg-gray-100 text-gray-800' :
                                    'bg-blue-100 text-blue-800'
                                  }`}>
                                    {alloc.membership_tier || 'N/A'}
                                  </span>
                                </td>
                                <td className="p-2 border text-center font-semibold text-green-600">+{alloc.rounds_allocated}</td>
                                <td className="p-2 border">{alloc.allocated_at ? parseUTCDate(alloc.allocated_at).toLocaleDateString() : 'N/A'}</td>
                                <td className="p-2 border">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    alloc.allocation_type === 'automatic' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                                  }`}>
                                    {alloc.allocation_type || 'manual'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Close Button */}
              <Button variant="outline" onClick={() => setFreeRoundsReportModal(false)} className="w-full">
                Close Report
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Mhala Hole-in-One Competition Stats Modal */}
        {/* Prize Giving Dinner Report Modal */}
        <Dialog open={dinnerReportModal} onOpenChange={setDinnerReportModal}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dinner-report-modal">
            <DialogHeader>
              <DialogTitle>🍽️ Prize Giving Dinner — {dinnerReport?.event_name}</DialogTitle>
            </DialogHeader>
            {dinnerReport && (
              <div className="space-y-4">
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-800" data-testid="dinner-total-players">{dinnerReport.total_players}</div>
                    <div className="text-xs text-purple-600">Players</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-800" data-testid="dinner-total-partners">{dinnerReport.total_partners}</div>
                    <div className="text-xs text-purple-600">Partners</div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-800" data-testid="dinner-total-headcount">{dinnerReport.total_headcount}</div>
                    <div className="text-xs text-purple-600">Total Headcount</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-800" data-testid="dinner-total-revenue">R{dinnerReport.total_revenue.toFixed(2)}</div>
                    <div className="text-xs text-green-600">Dinner Revenue</div>
                  </div>
                </div>
                {dinnerReport.attendees.length === 0 ? (
                  <p className="text-center text-gray-500 py-6">No dinner attendees yet</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="py-2 pr-3">Player</th>
                        <th className="pr-3">Contact</th>
                        <th className="pr-3">Partner</th>
                        <th className="text-right pr-4">Fee</th>
                        <th>Ref</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dinnerReport.attendees.map((a, i) => (
                        <tr key={i} className="border-b" data-testid={`dinner-attendee-row-${i}`}>
                          <td className="py-2 font-medium pr-3">{a.user_name}</td>
                          <td className="text-xs text-gray-500 pr-3">{a.user_email}{a.user_phone ? ` · ${a.user_phone}` : ''}</td>
                          <td className="pr-3">{a.partner ? '✅ Yes' : '—'}</td>
                          <td className="text-right pr-4">R{(a.dinner_fee || 0).toFixed(2)}</td>
                          <td className="text-xs text-gray-500">{a.booking_reference}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={exportDinnerReportCSV} data-testid="dinner-export-csv-btn">Export CSV</Button>
                  <Button onClick={() => setDinnerReportModal(false)}>Close</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={mhalaStatsModal} onOpenChange={setMhalaStatsModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>🏆 Mhala Hole-in-One Competition</DialogTitle>
              <DialogDescription>
                Track competition entries and revenue for sponsor payment
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Date Range Filter */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>From Date</Label>
                  <Input
                    type="date"
                    value={mhalaDateRange.startDate}
                    onChange={(e) => setMhalaDateRange(prev => ({...prev, startDate: e.target.value}))}
                  />
                </div>
                <div>
                  <Label>To Date</Label>
                  <Input
                    type="date"
                    value={mhalaDateRange.endDate}
                    onChange={(e) => setMhalaDateRange(prev => ({...prev, endDate: e.target.value}))}
                  />
                </div>
              </div>
              
              <Button 
                onClick={fetchMhalaStats} 
                disabled={loadingMhalaStats}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                {loadingMhalaStats ? 'Loading...' : 'Generate Report'}
              </Button>
              
              {/* Summary Cards */}
              {mhalaStats && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="bg-yellow-50 border-yellow-200">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-yellow-700">{mhalaStats.total_entries}</div>
                          <div className="text-sm text-yellow-600">Total Entries</div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-emerald-50 border-emerald-200">
                      <CardContent className="pt-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-emerald-700">R{mhalaStats.total_revenue?.toFixed(2) || '0.00'}</div>
                          <div className="text-sm text-emerald-600">Total Revenue (Mhala Fees)</div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Payment Method Breakdown */}
                  {mhalaStats.payment_breakdown && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold mb-3">Payment Method Breakdown</h3>
                      <div className="grid grid-cols-4 gap-3 text-sm">
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="font-bold text-blue-600">{mhalaStats.payment_breakdown.card || 0}</div>
                          <div className="text-gray-500">Card/PayFast</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="font-bold text-purple-600">{mhalaStats.payment_breakdown.credits || 0}</div>
                          <div className="text-gray-500">Credits</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="font-bold text-green-600">{mhalaStats.payment_breakdown.free_round || 0}</div>
                          <div className="text-gray-500">Free Round</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded border">
                          <div className="font-bold text-gray-600">{mhalaStats.payment_breakdown.other || 0}</div>
                          <div className="text-gray-500">Other</div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Only Card/PayFast payments will appear in your PayFast dashboard. Credits and Free Round payments are internal.
                      </p>
                    </div>
                  )}
                  
                  {/* Events Breakdown Table */}
                  {mhalaStats.events && mhalaStats.events.length > 0 ? (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold">Breakdown by Event</h3>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={downloadMhalaCSV}
                          className="text-emerald-600"
                        >
                          📥 Export CSV
                        </Button>
                      </div>
                      <div className="rounded-md border">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-3 text-left font-medium">Event</th>
                              <th className="p-3 text-left font-medium">Date</th>
                              <th className="p-3 text-right font-medium">Entries</th>
                              <th className="p-3 text-right font-medium">Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mhalaStats.events.map((event, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-3">{event.event_name || 'Unknown'}</td>
                                <td className="p-3">{event.event_date ? new Date(event.event_date).toLocaleDateString() : 'N/A'}</td>
                                <td className="p-3 text-right">{event.entries}</td>
                                <td className="p-3 text-right font-medium">R{event.revenue?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-yellow-50 font-semibold">
                            <tr>
                              <td className="p-3" colSpan={2}>Total</td>
                              <td className="p-3 text-right">{mhalaStats.total_entries}</td>
                              <td className="p-3 text-right text-emerald-700">R{mhalaStats.total_revenue?.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No Mhala competition entries found for the selected date range
                    </div>
                  )}
                  
                  {/* Detailed Entries List */}
                  {mhalaStats.detailed_entries && mhalaStats.detailed_entries.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold">Detailed Entries ({mhalaStats.detailed_entries.length})</h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={downloadMhalaDetailedCSV}
                          className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                        >
                          📥 Export CSV
                        </Button>
                      </div>
                      <div className="rounded-md border max-h-80 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="p-2 text-left font-medium">User</th>
                              <th className="p-2 text-left font-medium">Event</th>
                              <th className="p-2 text-left font-medium">Entry Date</th>
                              <th className="p-2 text-left font-medium">Payment</th>
                              <th className="p-2 text-right font-medium">Fee</th>
                            </tr>
                          </thead>
                          <tbody>
                            {mhalaStats.detailed_entries.map((entry, idx) => (
                              <tr key={idx} className="border-t hover:bg-gray-50">
                                <td className="p-2">
                                  <div className="font-medium">{entry.user_name}</div>
                                  <div className="text-xs text-gray-500">{entry.user_email}</div>
                                </td>
                                <td className="p-2 text-sm">{entry.event_name || 'Unknown'}</td>
                                <td className="p-2 text-sm">
                                  {entry.entry_date ? new Date(entry.entry_date).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="p-2">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    entry.payment_method === 'card' || entry.payment_method === 'payfast'
                                      ? 'bg-blue-100 text-blue-700'
                                      : entry.payment_method === 'credits'
                                      ? 'bg-purple-100 text-purple-700'
                                      : entry.payment_method === 'free_round'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {entry.payment_method || 'Unknown'}
                                  </span>
                                </td>
                                <td className="p-2 text-right font-medium">R{entry.mhala_fee?.toFixed(2) || '0.00'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setMhalaStatsModal(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Handicap Import Modal */}
        <Dialog open={handicapImportModal} onOpenChange={setHandicapImportModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>🏌️ Import Handicaps</DialogTitle>
              <DialogDescription>
                Upload a CSV file to update user handicaps in bulk
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* CSV Format Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📋 CSV Format Requirements</h4>
                <p className="text-blue-800 text-sm mb-3">Your CSV file should have the following columns:</p>
                <div className="bg-white border rounded p-3 font-mono text-sm overflow-x-auto">
                  <div className="font-semibold">email,dh_handicap,hna_handicap,gender,default_tee,is_new_player,has_hna</div>
                  <div className="text-gray-600">john@example.com,18.5,16.2,male,mens,false,true</div>
                  <div className="text-gray-600">jane@example.com,24.0,22.5,female,ladies,true,true</div>
                  <div className="text-gray-600">new@example.com,,,male,mens,true,false</div>
                </div>
                <div className="mt-3 text-blue-800 text-sm space-y-1">
                  <p><strong>email:</strong> User's email address (required)</p>
                  <p><strong>dh_handicap:</strong> DuckHook/DHG handicap value (optional)</p>
                  <p><strong>hna_handicap:</strong> HNA/SAGA handicap value (optional)</p>
                  <p><strong>gender:</strong> male / female (optional)</p>
                  <p><strong>default_tee:</strong> pro / mens / ladies / senior_01 / senior_02 / junior (optional)</p>
                  <p><strong>is_new_player:</strong> true / false (optional, default: false)</p>
                  <p><strong>has_hna:</strong> true / false (optional, default: true)</p>
                  <p className="text-blue-600 mt-2">💡 95% playing handicaps are auto-calculated. Leave blank any field you don't want to update.</p>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <Label htmlFor="handicap-file">Select CSV File</Label>
                <Input
                  type="file"
                  id="handicap-file"
                  accept=".csv"
                  onChange={(e) => setHandicapImportFile(e.target.files[0])}
                  className="mt-1"
                />
                {handicapImportFile && (
                  <p className="text-sm text-green-600 mt-2">
                    Selected: {handicapImportFile.name} ({(handicapImportFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {/* Progress Display */}
              {handicapImportProgress && (
                <div className={`border rounded-lg p-4 ${
                  handicapImportProgress.status === 'error' ? 'bg-red-50 border-red-200' :
                  handicapImportProgress.status === 'completed' ? 'bg-green-50 border-green-200' :
                  'bg-blue-50 border-blue-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {handicapImportProgress.status === 'uploading' && <div className="animate-spin">⏳</div>}
                    {handicapImportProgress.status === 'completed' && <div>✅</div>}
                    {handicapImportProgress.status === 'error' && <div>❌</div>}
                    <span className="font-medium">{handicapImportProgress.message}</span>
                  </div>
                </div>
              )}

              {/* Results Display */}
              {handicapImportResults && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-3">Import Results</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{handicapImportResults.total_processed || 0}</div>
                      <div className="text-sm text-gray-600">Total Processed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{handicapImportResults.successful_updates || 0}</div>
                      <div className="text-sm text-gray-600">Updated</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">{handicapImportResults.failed_updates || 0}</div>
                      <div className="text-sm text-gray-600">Failed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">{handicapImportResults.skipped || 0}</div>
                      <div className="text-sm text-gray-600">Skipped</div>
                    </div>
                  </div>

                  {handicapImportResults.errors && handicapImportResults.errors.length > 0 && (
                    <div className="mt-4">
                      <h5 className="font-semibold text-red-700 mb-2">Errors:</h5>
                      <div className="max-h-32 overflow-y-auto bg-white border rounded p-2">
                        {handicapImportResults.errors.map((error, index) => (
                          <div key={index} className="text-sm text-red-600">{error}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={handleHandicapImport}
                  disabled={!handicapImportFile || loading.handicapImport}
                  className="flex-1"
                >
                  {loading.handicapImport ? 'Importing...' : 'Import Handicaps'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setHandicapImportModal(false);
                    setHandicapImportFile(null);
                    setHandicapImportProgress(null);
                    setHandicapImportResults(null);
                  }}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Course Management Modal */}
        <Dialog open={courseModal} onOpenChange={setCourseModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedCourse ? 'Edit Course' : 'Add New Course'}</DialogTitle>
              <DialogDescription>
                Configure course details and tee configurations for playing handicap calculations.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Course Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Course Name *</Label>
                  <Input
                    value={courseForm.name}
                    onChange={(e) => setCourseForm({...courseForm, name: e.target.value})}
                    placeholder="e.g., Reading Country Club"
                  />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={courseForm.location}
                    onChange={(e) => setCourseForm({...courseForm, location: e.target.value})}
                    placeholder="e.g., Alberton"
                  />
                </div>
                <div>
                  <Label>Province</Label>
                  <Select
                    value={courseForm.province}
                    onValueChange={(value) => setCourseForm({...courseForm, province: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select province" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gauteng">Gauteng</SelectItem>
                      <SelectItem value="western_cape">Western Cape</SelectItem>
                      <SelectItem value="kwazulu_natal">KwaZulu-Natal</SelectItem>
                      <SelectItem value="eastern_cape">Eastern Cape</SelectItem>
                      <SelectItem value="free_state">Free State</SelectItem>
                      <SelectItem value="mpumalanga">Mpumalanga</SelectItem>
                      <SelectItem value="limpopo">Limpopo</SelectItem>
                      <SelectItem value="north_west">North West</SelectItem>
                      <SelectItem value="northern_cape">Northern Cape</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Tee Configurations */}
              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <Label className="text-base font-semibold">Tee Configurations *</Label>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={handleAddTee}
                  >
                    + Add Tee
                  </Button>
                </div>
                
                {courseForm.tees.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded">
                    <p>No tee configurations added yet.</p>
                    <p className="text-sm">Click "Add Tee" to configure tees for this course.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {courseForm.tees.map((tee, index) => (
                      <div key={index} className="border rounded p-3 bg-gray-50">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-sm">Tee #{index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-600 h-6 px-2"
                            onClick={() => handleRemoveTee(index)}
                          >
                            Remove
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div>
                            <Label className="text-xs">Tee Name</Label>
                            <Select
                              value={tee.tee_name}
                              onValueChange={(value) => handleUpdateTee(index, 'tee_name', value)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pro">Pro</SelectItem>
                                <SelectItem value="mens">Men's</SelectItem>
                                <SelectItem value="ladies">Ladies</SelectItem>
                                <SelectItem value="senior_01">Senior 01</SelectItem>
                                <SelectItem value="senior_02">Senior 02</SelectItem>
                                <SelectItem value="junior">Junior</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Course Rating</Label>
                            <Input
                              type="number"
                              step="0.1"
                              className="h-9"
                              value={tee.course_rating}
                              onChange={(e) => handleUpdateTee(index, 'course_rating', parseFloat(e.target.value) || 72)}
                              placeholder="e.g., 72.1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Slope Rating</Label>
                            <Input
                              type="number"
                              className="h-9"
                              value={tee.slope_rating}
                              onChange={(e) => handleUpdateTee(index, 'slope_rating', parseInt(e.target.value) || 113)}
                              placeholder="e.g., 128"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Par</Label>
                            <Input
                              type="number"
                              className="h-9"
                              value={tee.par}
                              onChange={(e) => handleUpdateTee(index, 'par', parseInt(e.target.value) || 72)}
                              placeholder="e.g., 72"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-3 text-xs text-gray-500 bg-blue-50 p-2 rounded">
                  <strong>Playing Handicap Formula:</strong> (HNA Index × Slope / 113) + (Course Rating - Par)
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCourseModal(false)}>Cancel</Button>
              <Button onClick={handleSaveCourse} className="bg-emerald-600 hover:bg-emerald-700">
                {selectedCourse ? 'Update Course' : 'Create Course'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

export default AdminPanel;